import React, { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../components/AuthContext';
import { notifyInvoiceDiscrepancy } from '../../lib/materialRequestEmails';
import { canReconcileInvoice, canSeeVendorContact, canCloseLineForBilling } from '../../lib/permissions';
import OnboardingCallout from '../../components/OnboardingCallout';
import OrderIdentifiers from '../../components/OrderIdentifiers';
import { matchesOrderSearch } from '../../lib/orderIdentifiers';
import { INVOICE, RECEIPT, canCloseForInvoice, closeDetail, setLineCloseFlag } from '../../lib/shortClose';
import {
  isOpenDiscrepancy,
  isResolved,
  resolutionDetail,
  firstPassRate as computeFirstPassRate,
  setInvoiceResolution,
  outcomeFor,
  EVENT_DISCREPANCY_RESOLVED,
  INVOICING_DOMAIN,
} from '../../lib/invoiceDiscrepancy';
import ResolveDiscrepancySheet from '../../components/inventory/ResolveDiscrepancySheet';
import RecordCreditSheet from '../../components/inventory/RecordCreditSheet';
import SendToQuickBooksSheet from '../../components/inventory/SendToQuickBooksSheet';
import BatchDocumentsSheet from '../../components/inventory/BatchDocumentsSheet';
import PurchaseOrderDownload from '../../components/purchasing/PurchaseOrderDownload';
import { getCompanyName } from '../../lib/tenant';
import { isUnexported, selectionTotal } from '../../lib/invoiceDocuments';
import {
  isCredit,
  isInvoiceDoc,
  billedTotal,
  creditedTotal,
  netInvoiced,
  remainingCreditable,
  enterVendorCredit,
} from '../../lib/vendorCredit';
import { sendNotification } from '../../lib/notify';
import Overlay from '../../components/Overlay';
import VendorContactCard from '../../components/inventory/VendorContactCard';
import ShortCloseSheet from '../../components/inventory/ShortCloseSheet';
import { itemLabel, itemUom } from '../../lib/requestItems';
import {
  invoiceableItems,
  itemBilledQty,
  itemOutstandingQty,
  itemReceiptSettled,
  hasInvoiceableLines,
  itemOrderedPrice,
  PAYMENT_TERMS,
  dueDateFromTerms,
  todayInputValue,
} from '../../lib/invoicing';
import {
  IconAlertTriangle,
  IconClock,
  IconLock,
  IconSearch,
  IconFileInvoice,
} from '@tabler/icons-react';

// #284, Session 74z. Invoicing is its own domain, not a section of Purchasing:
// its own colour, its own role scoping, and (74z2) its own notification family.
// The colour was chosen at 74y's mockup gate by mapping the five locked domain
// hues and taking the widest remaining gap — magenta sits ~55 degrees from Work
// Orders and ~60 from Safety. Pink was the first proposal and lost the check:
// the pink/red family is already this application's error language.
const MAGENTA = '#B31F7C';
const MAGENTA_SOFT = '#FBEDF5';
const MAGENTA_LINE = '#EFC8DF';

// The Settled tab reads closed history, which grows without bound. Capped, and
// the cap is stated on screen rather than left to look like the whole truth.
const SETTLED_LIMIT = 50;

function fmtDate(ts) {
  if (!ts) return '—';
  const d = new Date(ts);
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

// `invoice_date` and `due_date` are DATE columns, so PostgREST hands back a bare
// 'YYYY-MM-DD' with no zone. new Date('2026-08-20') parses that as UTC midnight,
// which toLocaleDateString then renders in local time — one day EARLIER
// everywhere west of Greenwich, which is everywhere D&R operates.
//
// Found by live test at 74z, not by reading: an invoice entered as Aug 20 with
// Net 30 rendered "Aug 19 · due Sep 18" against a sheet that had just computed
// Sep 19. Both dates feed AP aging and days-payable-outstanding (#285), so a
// silent one-day shift is a money-path defect rather than a display quirk.
//
// Parsed at local noon, which is the same guard dueDateFromTerms already applies
// for the identical reason. The regex is what keeps it safe: anything carrying a
// time component is already zoned and must not have one bolted on.
const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;
function fmtDateOnly(value) {
  if (!value) return '—';
  const d = DATE_ONLY.test(value) ? new Date(value + 'T12:00:00') : new Date(value);
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function ageDays(ts) {
  if (!ts) return 0;
  return Math.floor((Date.now() - new Date(ts)) / 86400000);
}

function money(n) {
  return '$' + (n != null ? Number(n) : 0).toFixed(2);
}

// A request is still on order when any line has something outstanding on it.
// #284's both-at-once case: a partially received order is legitimately Ready to
// invoice AND Still on order at the same time, so it renders here and in
// Jennifer's On Order tab simultaneously. Two badges, because a single status
// word cannot express it.
function isStillOnOrder(req) {
  return (req?.material_request_items || []).some(it => !itemReceiptSettled(it));
}

function requestInvoices(req) {
  return req?.material_request_invoices || [];
}

// #301, Session 74z4. "Flagged" and "open" used to be the same thing, because
// nothing could ever clear the flag. They are now different: the flag is
// permanent history and the resolution is what takes an order off this tab.
function hasOpenDiscrepancy(req) {
  return requestInvoices(req).some(isOpenDiscrepancy);
}

function requestHeadline(req) {
  const items = req?.material_request_items || [];
  if (items.length === 0) return req?.request_number || '—';
  return items.length > 1
    ? itemLabel(items[0]) + ' +' + (items.length - 1) + ' more'
    : itemLabel(items[0]);
}

function orderedTotalOf(req) {
  const items = req?.material_request_items || [];
  const lineSum = items.reduce((sum, it) => {
    const p = itemOrderedPrice(req, it, items.length);
    return sum + (p != null ? p : 0);
  }, 0);
  if (lineSum > 0) return lineSum;
  return req?.ordered_price != null ? parseFloat(req.ordered_price) : null;
}

// ── TOP-LEVEL COMPONENTS (hoisted to prevent the remount bug, locked rule 2) ──

function Badge({ tone, children }) {
  const tones = {
    magenta: { bg: MAGENTA_SOFT, fg: MAGENTA, br: MAGENTA_LINE },
    amber: { bg: '#FFF8EC', fg: '#8A5A08', br: '#F3D9A8' },
    grey: { bg: '#F3F3F3', fg: '#6E6E6E', br: '#E0E0E0' },
    red: { bg: '#FFEBEE', fg: '#A32D2D', br: '#F3C6C6' },
    green: { bg: '#E1F5EE', fg: '#0F6E56', br: '#B9E2D3' },
  };
  const t = tones[tone] || tones.grey;
  return (
    <span style={{
      fontSize: 9.5, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase',
      borderRadius: 4, padding: '2px 6px', whiteSpace: 'nowrap',
      background: t.bg, color: t.fg, border: '1px solid ' + t.br,
    }}>{children}</span>
  );
}

function StatusBadges({ req }) {
  const ready = hasInvoiceableLines(req);
  const onOrder = isStillOnOrder(req);
  const flagged = hasOpenDiscrepancy(req);
  return (
    <>
      {flagged && <Badge tone="red">Discrepancy</Badge>}
      {ready && <Badge tone="magenta">Ready to invoice</Badge>}
      {onOrder && <Badge tone="amber">Still on order</Badge>}
      {!ready && !onOrder && !flagged && <Badge tone="green">Settled</Badge>}
    </>
  );
}

function StatCell({ value, label, color, last }) {
  return (
    <div style={{ padding: '12px 20px', borderRight: last ? 'none' : '1px solid #E8E8E8', textAlign: 'center' }}>
      <div style={{ fontSize: 24, fontWeight: 700, color: color || '#0F0F0F' }}>{value}</div>
      <div style={{ fontSize: 11, color: '#7A7A7A', marginTop: 1 }}>{label}</div>
    </div>
  );
}

// ── #300, Session 74z3: aging ────────────────────────────────────────────────
//
// #285 added invoice_date, due_date and payment_terms at 74x for a stated
// purpose — "from which no aging, days-payable-outstanding or on-time-payment
// rate can be computed" — and nothing had computed any of the three. The
// columns were already selected on every request, so this is a derivation and a
// render with no schema change, which is why it rides with #288.
//
// Its own strip rather than a sixth stat cell. The stat row is four cells in a
// hardcoded four-column grid and all four are occupied; more to the point, an
// aging view exists to compare buckets against each other, and collapsed into
// one number it answers nothing.
const AGING_BUCKETS = [
  { key: 'current', label: 'Current', short: 'Current', edge: '#B9E2D3' },
  { key: 'd30', label: '1–30 days', short: '1–30', edge: '#EFC8DF' },
  { key: 'd60', label: '31–60 days', short: '31–60', edge: '#F3D9A8' },
  { key: 'd90', label: '61–90 days', short: '61–90', edge: '#E8B27E', ink: '#8A5A08' },
  { key: 'over', label: '90+ days', short: '90+', edge: '#D98080', ink: '#A32D2D', ground: '#FFFBFB' },
];

// Built at noon rather than midnight, the same reason dueDateFromTerms is: a
// midnight local Date crosses a day boundary under some timezone offsets, and
// an invoice would flip between Current and 1–30 depending on where the browser
// thinks it is.
function daysPastDue(dueDate, nowMs) {
  if (!dueDate) return null;
  const due = new Date(dueDate + 'T12:00:00').getTime();
  if (Number.isNaN(due)) return null;
  return Math.floor((nowMs - due) / 86400000);
}

function bucketKeyFor(days) {
  if (days == null) return null;
  if (days <= 0) return 'current';
  if (days <= 30) return 'd30';
  if (days <= 60) return 'd60';
  if (days <= 90) return 'd90';
  return 'over';
}

// Every invoice Stede holds, aged by its own due date.
//
// It is deliberately not filtered to open orders. Stede has no idea what has
// been paid — Colby's 74z2 decision put billing and payment permanently in
// QuickBooks — so what this measures is obligations Stede is holding and has
// not yet handed over, and an invoice on a settled order is still one of those.
// #298 SHIPPED THAT EXPORTED STATE AT SESSION 78n9.3, so this now narrows as
// the comment above promised — and it had to, because the sentence rendered
// beside these numbers reads "Unexported obligations. Payment status lives in
// QuickBooks." Counting handed-over invoices under that label was a caption
// disagreeing with its own figures.
//
// Handing a bill to QuickBooks is the moment Stede stops being the system of
// record for it. What is left here is what Stede is still holding, which is
// exactly what the aging strip claims to measure.
function computeAging(invoices, nowMs) {
  const totals = {};
  AGING_BUCKETS.forEach(b => { totals[b.key] = { amount: 0, count: 0 }; });
  let undated = 0;
  let grand = 0;

  invoices
    .filter(inv => !inv.exported_at && !inv.quickbooks_bill_id)
    .forEach(inv => {
    const amount = inv.total_amount != null ? Number(inv.total_amount) : 0;
    const key = bucketKeyFor(daysPastDue(inv.due_date, nowMs));
    // #429, Session 78n9.4. A credit is stored POSITIVE (#299), so every
    // consumer applies the sign itself — and this one did not, so a vendor
    // credit RAISED the total of what D&R owes. Found by live test on staging
    // while building the Unexported tab: the mobile strip's TOTAL read
    // $3,637.50 against a true net of $3,587.50, a $50 swing on one $25 credit.
    //
    // It only ever showed on MOBILE, which is why it survived: `grand` is
    // rendered in the mobile strip's sixth cell and nowhere on desktop. The
    // buckets were never wrong — a credit carries no due date, so it lands in
    // `undated` and never reaches one.
    grand += isCredit(inv) ? -amount : amount;
    // An invoice with no due date cannot be aged, and guessing a bucket for it
    // would be inventing the fact the whole strip is about. It is counted
    // separately and said out loud rather than folded into Current.
    if (!key) { undated += 1; return; }
    totals[key].amount += amount;
    totals[key].count += 1;
  });

  return { totals, undated, grand };
}

function AgingStrip({ aging, isMobile }) {
  const cell = (b, compact) => {
    const t = aging.totals[b.key];
    return (
      <div key={b.key} style={{
        border: '1px solid #E8E8E8', borderTop: '3px solid ' + b.edge,
        borderRadius: '0 0 7px 7px', padding: compact ? '7px 8px 8px' : '8px 10px 9px',
        background: b.ground || '#FCFCFB',
      }}>
        <div style={{
          fontSize: compact ? 15 : 17, fontWeight: 700, lineHeight: 1.15,
          color: t.amount > 0 && b.ink ? b.ink : '#0F0F0F',
          fontVariantNumeric: 'tabular-nums',
        }}>{money(t.amount)}</div>
        <div style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: 0.6, textTransform: 'uppercase', color: '#7A7A7A', marginTop: 2 }}>
          {compact ? b.short : b.label}
        </div>
        {!compact && (
          <div style={{ fontSize: 10.5, color: '#9A9A9A', marginTop: 1 }}>
            {t.count} invoice{t.count === 1 ? '' : 's'}
          </div>
        )}
      </div>
    );
  };

  // The sentence is load-bearing rather than a caption. Without it this reads as
  // an AP aging report and is simply wrong: Stede cannot know what has been
  // paid, only what it is still holding.
  const note = 'Unexported obligations. Payment status lives in QuickBooks.';

  if (isMobile) {
    return (
      <div style={{ background: '#fff', borderBottom: '1px solid #E8E8E8', padding: '11px 16px 13px' }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: '#7A7A7A', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 7 }}>
          Aging by due date
        </div>
        {/* Three by two, keeping all five buckets plus a total. Truncating to
            three would drop the tail, and the tail is the reason to open it. */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6 }}>
          {AGING_BUCKETS.map(b => cell(b, true))}
          <div style={{
            border: '1px solid #E8E8E8', borderTop: '3px solid #E0E0E0',
            borderRadius: '0 0 7px 7px', padding: '7px 8px 8px', background: '#FAFAF9',
          }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#0F0F0F', lineHeight: 1.15, fontVariantNumeric: 'tabular-nums' }}>
              {money(aging.grand)}
            </div>
            <div style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: 0.6, textTransform: 'uppercase', color: '#7A7A7A', marginTop: 2 }}>
              Total
            </div>
          </div>
        </div>
        <div style={{ fontSize: 11, color: '#9A9A9A', marginTop: 8 }}>{note}</div>
        {aging.undated > 0 && (
          <div style={{ fontSize: 11, color: '#9A9A9A', marginTop: 2 }}>
            {aging.undated === 1
              ? '1 invoice carries no due date and cannot be aged.'
              : aging.undated + ' invoices carry no due date and cannot be aged.'}
          </div>
        )}
      </div>
    );
  }

  return (
    <div style={{ background: '#fff', borderBottom: '1px solid #E8E8E8', padding: '11px 16px 13px', flexShrink: 0 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 10, marginBottom: 9, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: '#7A7A7A', textTransform: 'uppercase', letterSpacing: 0.5 }}>
          Aging by due date
        </div>
        <div style={{ fontSize: 11, color: '#9A9A9A' }}>
          {note}
          {aging.undated > 0 && ' · ' + aging.undated + ' with no due date, not aged'}
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 8 }}>
        {AGING_BUCKETS.map(b => cell(b, false))}
      </div>
    </div>
  );
}

// onSelect rather than setActiveTab directly: switching tabs also clears the
// selected order, because the detail panel otherwise keeps rendering a row that
// is not on the tab beside it — an empty list next to a populated detail, which
// reads as a filter that failed rather than a tab that is genuinely empty.
// discrepancyTabRef — callouts gate Wave 2, inq_tabs_first. One ref, one element.
function TabBar({ tabs, activeTab, setActiveTab, isMobile, discrepancyTabRef }) {
  if (isMobile) {
    // #284's mobile answer, drawn at the gate rather than assumed to follow the
    // desktop: the tab split survives at phone width as a horizontal chip rail.
    return (
      <div style={{ display: 'flex', gap: 6, overflowX: 'auto', padding: '9px 12px', background: 'white', borderBottom: '1px solid #E8E8E8' }}>
        {tabs.map(t => (
          <button key={t.id} ref={t.id === 'discrepancies' ? discrepancyTabRef : undefined} onClick={() => setActiveTab(t.id)}
            style={{
              fontSize: 11, fontWeight: 700, padding: '6px 10px', borderRadius: 14, whiteSpace: 'nowrap',
              border: 'none', cursor: 'pointer',
              background: activeTab === t.id ? MAGENTA : '#F0F0F0',
              color: activeTab === t.id ? 'white' : '#5C5C5C',
            }}>
            {(t.short || t.label)}{t.count != null ? ' ' + t.count : ''}
          </button>
        ))}
      </div>
    );
  }
  return (
    <div style={{ display: 'flex', borderBottom: '1px solid #E8E8E8', padding: '0 16px', background: '#F7F7F6', flexShrink: 0 }}>
      {tabs.map(t => {
        const on = activeTab === t.id;
        return (
          <button key={t.id} ref={t.id === 'discrepancies' ? discrepancyTabRef : undefined} onClick={() => setActiveTab(t.id)}
            style={{
              padding: '11px 15px', fontSize: 12.5, fontWeight: 600, whiteSpace: 'nowrap',
              display: 'flex', alignItems: 'center', gap: 7, cursor: 'pointer',
              // Longhand on all four sides. Mixing the `border` shorthand with a
              // `borderBottom` longhand on the same element makes React warn on every
              // rerender and drop one of them - the same defect 74t fixed in
              // Overlay.js. Surfaced here by live test, in the console rather than on
              // screen, which is why the console gets read rather than glanced at.
              borderTop: 'none', borderLeft: 'none', borderRight: 'none',
              borderBottom: '2px solid ' + (on ? MAGENTA : 'transparent'),
              background: on ? 'white' : 'transparent',
              color: on ? MAGENTA : '#7A7A7A',
            }}>
            {t.label}
            {t.count != null && (
              <span style={{
                fontSize: 10, fontWeight: 700, borderRadius: 9, padding: '1px 6px', minWidth: 18, textAlign: 'center',
                background: on ? MAGENTA : '#F0F0F0', color: on ? 'white' : '#5C5C5C',
              }}>{t.count}</span>
            )}
          </button>
        );
      })}
    </div>
  );
}

function InvoiceRequestCard({ req, isMobile, selectedReq, onSelect, branchName }) {
  const items = req.material_request_items || [];
  const settledLines = items.filter(itemReceiptSettled).length;
  // Found by live test, not by reading. This line first counted fully-settled
  // lines and called them "received", so DAL-REQ-00047 - one line, 10 ordered,
  // 4 arrived and billable - announced "0 of 1 lines received" on the card while
  // the detail panel beside it showed 4 received and 4 invoiceable.
  //
  // It is the same defect #284's own entry recorded on the buyer's card ("0 of 1
  // item tracked ... reads as nothing has arrived"), reproduced on AP's surface.
  // Bridgette's question is not how many lines are finished, it is how many she
  // can bill right now, so that is what the card answers.
  const billableLines = invoiceableItems(items).length;
  const isSelected = !isMobile && selectedReq?.id === req.id;
  const total = orderedTotalOf(req);
  // #296. What has already been billed on this order, so a row carrying no
  // billable lines right now still says why it is here. Without it, "0 of 1
  // lines ready to bill" reads as an order that should not be on the tab.
  const invoices = requestInvoices(req);
  // #429, Session 78n9.4. Was a bare sum over every document, which added
  // credits to what had been billed — so an order carrying a credit reported
  // MORE billed than the vendor ever charged. netInvoiced() is the function
  // built for this at #299 and subtracts them, which is what "billed so far"
  // has to mean beside an ordered total the reader is comparing it against.
  //
  // The second and last instance of the class; the sweep that found it
  // enumerated every summation of total_amount in src/ rather than stopping at
  // the one that surfaced.
  const billedSoFar = netInvoiced(invoices);
  const showBilled = invoices.length > 0 && billableLines === 0 && isStillOnOrder(req);

  return (
    <div
      onClick={() => onSelect(req)}
      style={{
        background: 'white', border: '1px solid ' + (isSelected ? MAGENTA : '#E8E8E8'),
        boxShadow: isSelected ? '0 0 0 2px rgba(179,31,124,.13)' : 'none',
        borderRadius: 9, padding: '12px 13px', marginBottom: 8, cursor: 'pointer',
      }}>
      <div style={{ fontSize: 10, color: '#9A9A9A', letterSpacing: 0.4 }}>
        {req.request_number}{req.po_ref ? ' · PO ' + req.po_ref : ''}
      </div>
      <div style={{ fontWeight: 700, fontSize: 14, margin: '3px 0 4px' }}>{requestHeadline(req)}</div>
      <div style={{ fontSize: 11.5, color: '#5C5C5C' }}>
        {branchName}
        {/* #288, 74z3. "Fully received" was true when the only way to settle a
            line was for everything to arrive. A short-closed line is settled
            WITHOUT having arrived, so this now says what it actually means.
            Caught on the first live test: an order with 4 of 10 sheets received
            described itself as fully received. */}
        {items.length > 0 && settledLines < items.length
          ? ' · ' + billableLines + ' of ' + items.length + ' lines ready to bill'
          : (items.some(it => it.receipt_closed) ? ' · settled short' : ' · fully received')}
        {' · ' + ageDays(req.updated_at) + 'd'}
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
        <span style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          <StatusBadges req={req} />
          {showBilled && (
            <Badge tone="grey">Billed {money(billedSoFar)}{total != null ? ' of ' + money(total) : ''}</Badge>
          )}
        </span>
        {total != null && (
          <span style={{ fontSize: 12, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{money(total)}</span>
        )}
      </div>
    </div>
  );
}

// #298, Session 78n9.4. Gate screen 3's row.
//
// The other three tabs list ORDERS. This one lists INVOICES, and that is the
// gate's own shape rather than an inconsistency: the artifacts produced from
// this selection are per-invoice documents and a per-invoice register, and the
// third action on the bar — Send to QuickBooks — takes an invoice id. A tab
// that selected orders would have to pick an invoice for you at export time.
//
// The whole row toggles, not just the checkbox. A 14px target is not a tap
// target on the priority surface, and the row carries nothing else clickable.
function UnexportedInvoiceRow({ invoice, request, branchName, checked, onToggle }) {
  const credit = isCredit(invoice);
  const amount = invoice.total_amount != null ? parseFloat(invoice.total_amount) : 0;
  const flagged = invoice.discrepancy_flagged && !invoice.discrepancy_resolved;

  return (
    <div
      onClick={() => onToggle(invoice.id)}
      role="checkbox"
      aria-checked={checked}
      tabIndex={0}
      onKeyDown={e => {
        if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); onToggle(invoice.id); }
      }}
      style={{
        background: 'white', border: '1px solid ' + (checked ? MAGENTA : '#E8E8E8'),
        boxShadow: checked ? '0 0 0 2px rgba(179,31,124,.13)' : 'none',
        borderRadius: 9, padding: '11px 12px', marginBottom: 8, cursor: 'pointer',
        display: 'flex', gap: 11, alignItems: 'flex-start',
      }}>
      <span
        aria-hidden="true"
        style={{
          flexShrink: 0, width: 17, height: 17, borderRadius: 4, marginTop: 1,
          border: '1.5px solid ' + (checked ? MAGENTA : '#C8C8C8'),
          background: checked ? MAGENTA : 'white',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: 'white', fontSize: 11, fontWeight: 700, lineHeight: 1,
        }}>
        {checked ? '✓' : ''}
      </span>

      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'baseline' }}>
          <span style={{ fontWeight: 700, fontSize: 13.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {invoice.invoice_ref || '(no reference)'}
          </span>
          {/* A credit is stored positive per #299, so every surface applies the
              sign itself. Shown negative here because on a list of things D&R
              owes, a credit is the one row that reduces the number. */}
          <span style={{
            fontSize: 12.5, fontWeight: 700, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap',
            color: credit ? '#0F6E56' : '#0F0F0F',
          }}>
            {credit ? '-' : ''}{money(amount)}
          </span>
        </span>
        <div style={{ fontSize: 11.5, color: '#5C5C5C', marginTop: 3 }}>
          {request?.inventory_vendors?.name || 'Unknown vendor'} · {branchName}
        </div>
        <div style={{ fontSize: 11, color: '#9A9A9A', marginTop: 2 }}>
          {invoice.invoice_date ? fmtDateOnly(invoice.invoice_date) : 'no date'}
          {invoice.due_date ? ' · due ' + fmtDateOnly(invoice.due_date) : ''}
          {request?.po_ref ? ' · PO ' + request.po_ref : (request?.request_number ? ' · ' + request.request_number : '')}
        </div>
        {(credit || flagged) && (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 7 }}>
            {credit && <Badge tone="grey">Vendor credit</Badge>}
            {flagged && <Badge tone="red">Unresolved discrepancy</Badge>}
          </div>
        )}
      </span>
    </div>
  );
}

// The bar the gate draws under the list, and its mobile callout is the reason
// the running TOTAL is here rather than only the count: Bridgette's next
// question after "how many" is always "how much", and it costs nothing to
// answer before she asks.
function SelectionBar({ count, total, isMobile, onExport, onSendQb, onClear, canSendQb }) {
  if (count === 0) return null;

  const qbDisabled = !canSendQb || count !== 1;
  // #431, Session 78n9.4, raised by Colby asking which tab Bridgette sends from
  // and whether a new user would know.
  //
  // The reason a disabled control gives has to reach a THUMB. It lived only in
  // a `title` tooltip, which touch never shows -- so on the priority surface
  // this was a button that did nothing and said nothing. That is the dead-end
  // shape #425 recorded one session ago and 78n8.2's blocker before it, and it
  // shipped again here.
  const qbReason = !canSendQb ? 'You do not have permission to send to QuickBooks'
    : count > 1 ? 'QuickBooks takes one invoice at a time. Tick a single row to send it.'
      : null;

  const summary = count + ' selected · ' + money(total);

  // The verb was missing. It said "QuickBooks" -- a noun -- for the same action
  // the invoice detail calls "Send to QuickBooks". Full label where there is
  // room; "Send" on mobile, where the bar was already 358px into 349px.
  const sendLabel = isMobile ? 'Send' : 'Send to QuickBooks';

  const sendBtn = (extra) => (
    <button
      onClick={onSendQb}
      disabled={qbDisabled}
      title={qbReason || 'Send this invoice to QuickBooks as a bill'}
      style={{
        border: '1px solid ' + (isMobile ? 'rgba(255,255,255,.55)' : '#E0E0E0'),
        background: 'transparent', borderRadius: 7, padding: '8px 11px',
        fontSize: 12, fontWeight: 700, cursor: qbDisabled ? 'not-allowed' : 'pointer',
        color: isMobile ? 'white' : '#5C5C5C', opacity: qbDisabled ? 0.5 : 1,
        ...extra,
      }}>
      {sendLabel}
    </button>
  );

  const exportBtn = (extra) => (
    <button
      onClick={onExport}
      style={{
        border: 'none', borderRadius: 7, padding: '8px 13px',
        fontSize: 12, fontWeight: 700, cursor: 'pointer',
        background: isMobile ? 'white' : MAGENTA,
        color: isMobile ? MAGENTA : 'white',
        ...extra,
      }}>
      Export
    </button>
  );

  const clearBtn = (
    <button
      onClick={onClear}
      style={{
        border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 11.5, fontWeight: 600,
        color: isMobile ? 'rgba(255,255,255,.85)' : '#7A7A7A', padding: '5px 2px',
      }}>
      Clear
    </button>
  );

  const reason = qbReason && (
    <div style={{
      fontSize: 11, lineHeight: 1.45, borderRadius: 6, padding: '6px 9px',
      background: isMobile ? 'rgba(0,0,0,.18)' : '#FBEDF5',
      color: isMobile ? 'white' : '#7A1355',
    }}>
      {qbReason}
    </div>
  );

  // TWO EXPLICIT ROWS ON MOBILE rather than one row left to wrap. Found by
  // looking at it: the count, Clear and Send filled the first line and Export
  // -- the action that is ENABLED whenever this bar exists -- fell to a line of
  // its own and read as the lesser of the two. Wrapping decided which control
  // looked primary, and it chose wrong.
  //
  // Full-width halves also give each action a real tap target, which is the
  // 2-taps-under-3-seconds standard rather than a nicety.
  if (isMobile) {
    return (
      <div style={{
        background: MAGENTA, borderRadius: 10, padding: '11px 13px',
        boxShadow: '0 4px 14px rgba(0,0,0,.18)', display: 'flex', flexDirection: 'column', gap: 9,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 12.5, fontWeight: 700, color: 'white', fontVariantNumeric: 'tabular-nums' }}>
            {summary}
          </span>
          <span style={{ flex: 1 }} />
          {clearBtn}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {sendBtn({ flex: 1 })}
          {exportBtn({ flex: 1 })}
        </div>
        {reason}
      </div>
    );
  }

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
      background: 'white', border: '1px solid ' + MAGENTA_LINE, borderRadius: 9, padding: '10px 12px',
    }}>
      <span style={{ fontSize: 12.5, fontWeight: 700, color: '#0F0F0F', fontVariantNumeric: 'tabular-nums' }}>
        {summary}
      </span>
      <span style={{ flex: 1 }} />
      {clearBtn}
      {sendBtn()}
      {exportBtn()}
      {reason && <div style={{ flexBasis: '100%' }}>{reason}</div>}
    </div>
  );
}

function LineTable({ req, canShortClose, onShortClose, onReopen }) {
  const items = req.material_request_items || [];
  const headers = ['Line', 'Ordered', 'Received', 'Invoiceable', 'Status', ''];
  // #288, 74z3. The action column made this table wider than the detail panel
  // at narrow desktop widths — measured at 326px against a 288px panel on an
  // 842px viewport, with the parent set to overflow: visible, so the Status
  // badge was clipped with no way to reach it. Found by live test, not by
  // reading. The table scrolls inside its own container rather than the panel
  // scrolling sideways.
  return (
    <div style={{ overflowX: 'auto', margin: '6px 0 4px' }}>
    <table style={{ width: '100%', minWidth: 360, borderCollapse: 'collapse', fontSize: 13 }}>
      <thead>
        <tr>
          {headers.map((h, i) => (
            <th key={i} style={{
              textAlign: i === 0 || i >= 4 ? 'left' : 'right',
              fontSize: 9.5, letterSpacing: 0.9, textTransform: 'uppercase', color: '#9A9A9A',
              fontWeight: 700, padding: '0 8px 7px 0', borderBottom: '1px solid #E8E8E8',
            }}>{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {items.map(it => {
          const received = it.qty_received != null ? parseFloat(it.qty_received) : null;
          const outstanding = itemOutstandingQty(it);
          const settled = itemReceiptSettled(it);
          const billed = itemBilledQty(it);
          let tone = 'amber';
          let label = 'Not arrived';
          if (received != null && received > 0 && !settled) { label = 'Part received'; }
          if (settled) {
            tone = 'green';
            label = outstanding <= 0.0001 && billed > 0 ? 'Billed' : 'Complete';
          }
          // The receipt flag is the buyer's, so it renders here in grey — AP can
          // see that Purchasing declared the balance dead, and cannot change it.
          if (it.receipt_closed) { tone = 'grey'; label = 'Closed short'; }
          const cell = { padding: '10px 8px 10px 0', borderBottom: '1px solid #F0F0F0' };
          const num = { ...cell, textAlign: 'right', fontVariantNumeric: 'tabular-nums' };
          // Both reasons, not just AP's. The receipt reason is Purchasing's
          // declaration and it renders nowhere else on this page — it is the
          // answer to "why is this line dead", which is the question AP is
          // actually asking when they meet a short-closed line.
          const receiptDetail = closeDetail(it, RECEIPT);
          const detail = closeDetail(it, INVOICE);
          return (
            <tr key={it.id}>
              <td style={cell}>
                {itemLabel(it)}
                {/* #288, 74z3. The reason lives under the line rather than in a
                    tooltip: it is the answer to "why did we pay $4,180 against a
                    $5,200 order", and an answer nobody can see is not one. */}
                {receiptDetail?.reason && (
                  <div style={{ fontSize: 11, color: '#7A7A7A', fontStyle: 'italic', marginTop: 2 }}>
                    Receiving: “{receiptDetail.reason}”{receiptDetail.byName ? ' — ' + receiptDetail.byName : ''}
                  </div>
                )}
                {detail?.reason && (
                  <div style={{ fontSize: 11, color: '#7A7A7A', fontStyle: 'italic', marginTop: 2 }}>
                    Billing: “{detail.reason}”{detail.byName ? ' — ' + detail.byName : ''}
                  </div>
                )}
              </td>
              <td style={num}>{it.qty_requested ?? '—'}</td>
              <td style={num}>{received != null ? received : '—'}</td>
              <td style={num}>{outstanding > 0.0001 ? outstanding : 0}</td>
              <td style={cell}>
                <span style={{ display: 'inline-flex', gap: 4, flexWrap: 'wrap' }}>
                  <Badge tone={tone}>{label}</Badge>
                  {/* #288, 74z3. invoice_closed rendered NOWHERE before this.
                      Without it AP sets the flag and watches the screen not
                      change, which is how a thing gets set twice. Magenta
                      because the act belongs to Invoicing; the receipt badge
                      stays grey because that one belongs to Purchasing. */}
                  {it.invoice_closed && <Badge tone="magenta">Billing closed</Badge>}
                </span>
              </td>
              <td style={cell}>
                {canShortClose && it.invoice_closed && (
                  <button
                    type="button"
                    onClick={() => onReopen(it)}
                    style={{
                      padding: '5px 9px', background: 'white', color: '#0F0F0F',
                      border: '1px solid #E0E0E0', borderRadius: 7, fontSize: 11,
                      fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap',
                    }}
                  >
                    Reopen
                  </button>
                )}
                {canShortClose && canCloseForInvoice(it) && (
                  <button
                    type="button"
                    onClick={() => onShortClose(it)}
                    style={{
                      padding: '5px 9px', background: 'white', color: '#0F0F0F',
                      border: '1px solid #E0E0E0', borderRadius: 7, fontSize: 11,
                      fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap',
                    }}
                  >
                    No more billing
                  </button>
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
    </div>
  );
}

function InvoiceHistory({ req, canResolve, onResolve, onCredit, onSendToQb, qbSentId }) {
  const docs = requestInvoices(req);
  if (docs.length === 0) return null;

  // #299, Session 74z5. Credits are ordered directly beneath the invoice they
  // correct rather than by date, because "what does this invoice actually come
  // to" is the question being answered and a credit read three rows away from
  // its invoice does not answer it.
  const invoices = docs.filter(isInvoiceDoc);
  const credited = creditedTotal(docs);
  const ordered = [];
  invoices.forEach(inv => {
    ordered.push(inv);
    docs.filter(d => isCredit(d) && String(d.credit_of_invoice_id) === String(inv.id))
      .forEach(c => ordered.push(c));
  });

  return (
    <div style={{ marginTop: 18 }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: '#7A7A7A', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 8 }}>
        Invoices on this order
      </div>
      {ordered.map(inv => {
        // #299. A credit is its own document, not an invoice with a minus sign.
        // It carries no discrepancy state at all — a credit cannot be flagged,
        // because it is the correction of a mismatch rather than one.
        if (isCredit(inv)) {
          return (
            <div key={'c' + inv.id} style={{
              border: '1px solid ' + MAGENTA_LINE, background: MAGENTA_SOFT,
              borderRadius: 8, padding: '9px 12px', marginBottom: 8,
              display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap',
            }}>
              <div>
                <div style={{ fontSize: 12.5, fontWeight: 700, color: MAGENTA }}>
                  {inv.invoice_ref || 'No credit number'}
                  <span style={{ marginLeft: 6 }}><Badge tone="magenta">Credit</Badge></span>
                </div>
                <div style={{ fontSize: 11, color: '#8A5F76', marginTop: 2 }}>
                  {fmtDateOnly(inv.invoice_date)}
                  {(() => {
                    const parent = invoices.find(i => String(i.id) === String(inv.credit_of_invoice_id));
                    return parent ? ' · credits ' + (parent.invoice_ref || 'the invoice above') : '';
                  })()}
                </div>
              </div>
              <div style={{ fontSize: 13, fontWeight: 700, color: MAGENTA, fontVariantNumeric: 'tabular-nums' }}>
                {'−' + money(inv.total_amount)}
              </div>
            </div>
          );
        }
        // #301, Session 74z4. Three states, not two. An unflagged invoice is
        // plain; an open discrepancy is red and offers the action; a resolved
        // one keeps the flag as history and says who ended it and how.
        const open = isOpenDiscrepancy(inv);
        const settled = isResolved(inv);
        const detail = resolutionDetail(inv);
        return (
        <div key={inv.id} style={{
          border: '1px solid ' + (open ? '#F3C6C6' : settled ? MAGENTA_LINE : '#E8E8E8'),
          background: open ? '#FFF5F6' : settled ? MAGENTA_SOFT : 'white',
          borderRadius: 8, padding: '9px 12px', marginBottom: 8,
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <div>
              <div style={{ fontSize: 12.5, fontWeight: 700 }}>{inv.invoice_ref || 'No invoice number'}</div>
              <div style={{ fontSize: 11, color: '#7A7A7A', marginTop: 2 }}>
                {fmtDateOnly(inv.invoice_date)}
                {inv.payment_terms ? ' · ' + inv.payment_terms : ''}
                {inv.due_date ? ' · due ' + fmtDateOnly(inv.due_date) : ''}
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 13, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{money(inv.total_amount)}</div>
                {open && <div style={{ marginTop: 3 }}><Badge tone="red">Discrepancy</Badge></div>}
                {settled && <div style={{ marginTop: 3 }}><Badge tone="magenta">Resolved</Badge></div>}
                {/* #298, 78n9.3. The exported state, and it is a fact about the
                    OUTSIDE world rather than about this row: the bill exists in
                    QuickBooks. Shown even to someone who cannot send, because
                    "has this been handed over" is the question the whole
                    Invoicing screen is about. */}
                {(inv.exported_at || inv.quickbooks_bill_id) && (
                  <div style={{ marginTop: 3 }}><Badge tone="green">In QuickBooks</Badge></div>
                )}
              </div>
              {canResolve && open && (
                <button
                  type="button"
                  onClick={() => onResolve(inv)}
                  style={{
                    padding: '6px 11px', background: 'white', color: '#A32D2D',
                    border: '1px solid #E4A9A9', borderRadius: 7, fontSize: 11.5,
                    fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap',
                  }}
                >
                  Resolve
                </button>
              )}
              {canResolve && settled && (
                <button
                  type="button"
                  onClick={() => onResolve(inv)}
                  style={{
                    padding: '6px 11px', background: 'white', color: '#7A7A7A',
                    border: '1px solid #E0E0E0', borderRadius: 7, fontSize: 11.5,
                    fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap',
                  }}
                >
                  Reopen
                </button>
              )}
              {/* #299. The action lives on the invoice, not on the page — you
                  cannot start a credit without having already chosen what it
                  credits, which is what makes credit_of_invoice_id structural
                  rather than a field someone can fill in wrong. Hidden once the
                  invoice is fully credited, because there is nothing left to
                  credit and an enabled control that always errors is worse than
                  no control. */}
              {canResolve && onCredit && remainingCreditable(inv, docs) > 0 && (
                <button
                  type="button"
                  onClick={() => onCredit(inv)}
                  style={{
                    padding: '6px 11px', background: 'white', color: MAGENTA,
                    border: '1px solid ' + MAGENTA_LINE, borderRadius: 7, fontSize: 11.5,
                    fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap',
                  }}
                >
                  Credit
                </button>
              )}
              {/* #298, gate screen 4. Offered only while there is something to
                  send: once exported the control disappears rather than going
                  disabled, because the badge beside it already says why and a
                  greyed Send invites a hunt for how to enable it.

                  Deliberately NOT hidden when the invoice has a blocker — an
                  unlinked supplier or a missing account is invisible from this
                  row, so hiding the button here would leave no way to find out
                  why. The sheet opens, states the reason, and refuses to send.
                  Gate rule G: block the button and say why. */}
              {onSendToQb && !inv.exported_at && !inv.quickbooks_bill_id && (
                <button
                  type="button"
                  onClick={() => onSendToQb(inv)}
                  style={{
                    padding: '6px 11px', background: 'white', color: '#0F6E56',
                    border: '1px solid #B9E2D3', borderRadius: 7, fontSize: 11.5,
                    fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap',
                  }}
                >
                  Send to QuickBooks
                </button>
              )}
            </div>
          </div>

          {/* The reversal panel. Without this a resolution is a badge with no
              author, and the reason — the only record of why a difference was
              paid or dismissed — would be written to the database and never
              shown to anyone. */}
          {detail && (
            <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid ' + MAGENTA_LINE, fontSize: 11, color: '#5C5C5C', lineHeight: 1.5 }}>
              <span style={{ fontWeight: 700, color: MAGENTA }}>{detail.outcome?.label || detail.code}</span>
              {detail.byName ? ' · ' + detail.byName : ''}
              {detail.at ? ' · ' + fmtDateOnly(detail.at) : ''}
              {detail.reason ? <div style={{ marginTop: 2, fontStyle: 'italic' }}>“{detail.reason}”</div> : null}
            </div>
          )}
        </div>
        );
      })}

      {/* #299. Shown only once a credit exists — on an uncredited order the
          billed figure and the net figure are the same number and printing it
          twice is noise. "Net invoiced" is the figure that was previously
          impossible to produce, and the reason this issue was HIGH. */}
      {credited > 0 && (
        <div style={{
          display: 'flex', justifyContent: 'flex-end', gap: 18, flexWrap: 'wrap',
          marginTop: 4, paddingTop: 10, borderTop: '1px solid #E8E8E8',
          fontSize: 12, color: '#7A7A7A', fontVariantNumeric: 'tabular-nums',
        }}>
          <span>Billed <b style={{ color: '#0F0F0F' }}>{money(billedTotal(docs))}</b></span>
          <span>Credited <b style={{ color: MAGENTA }}>{'−' + money(credited)}</b></span>
          <span>Net invoiced <b style={{ color: '#0F0F0F' }}>{money(netInvoiced(docs))}</b></span>
        </div>
      )}
    </div>
  );
}

// #295, Session 74z2. What "View full order" used to promise, delivered where
// Bridgette already is.
//
// The button it replaces called setActive('purchasing_queue') with no request
// id, so it landed on the queue's default needs_action tab — which did not
// contain the `ordered` request it named. On mobile there was no detail panel
// either, so it read as the record having vanished. Found by Colby at 74z's
// close on the one surface that session never exercised.
//
// Decided by him the same day: expand in place, do not deep-link. That closes
// the second half of the defect by construction — there is no departure, so
// there is nothing to come back from — and it settles #284's decision-3 tension,
// because AP now never renders on the buyer's screen at all.
//
// Collapsed by default: this is reference material for a question that has come
// up, not part of reconciling. Everything needed to enter an invoice stays above
// it, unmoved.
function FullOrderDetail({ req, canSeeContact }) {
  const [open, setOpen] = useState(false);
  const items = req.material_request_items || [];
  // Tracking is per line as well as per request — MOCKUP_REFERENCE_212's Screen
  // 5 established that, and material_request_items carries its own carrier,
  // number and ETA. Showing only the request-level pair would hide the case a
  // multi-line order actually produces: two lines shipped separately.
  const tracked = items.filter(it => it.tracking_number || it.tracking_eta);
  const hasRequestTracking = req.tracking_number || req.tracking_eta;
  const itemNotes = items.filter(it => it.notes);
  const vendor = req.inventory_vendors;

  return (
    <div style={{ border: '1px solid #E8E8E8', borderRadius: 9, marginTop: 18, overflow: 'hidden' }}>
      <button
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: 10, padding: '11px 13px', background: '#FBFAF9', border: 'none', cursor: 'pointer',
          fontSize: 12.5, fontWeight: 700, color: '#0F0F0F', textAlign: 'left',
        }}>
        <span>Full order detail</span>
        <span style={{ fontSize: 11, fontWeight: 500, color: '#9A9A9A' }}>
          Tracking, notes, vendor contact {open ? '▴' : '▾'}
        </span>
      </button>

      {open && (
        <div style={{ padding: '4px 13px 14px', borderTop: '1px solid #F0F0F0' }}>
          <SubHead>Tracking</SubHead>
          {tracked.length === 0 && !hasRequestTracking && (
            <div style={{ fontSize: 12, color: '#9A9A9A', fontStyle: 'italic' }}>No tracking added to this order.</div>
          )}
          {hasRequestTracking && tracked.length === 0 && (
            <TrackRow
              label="Whole order"
              carrier={req.tracking_carrier}
              number={req.tracking_number}
              eta={req.tracking_eta}
            />
          )}
          {tracked.map(it => (
            <TrackRow
              key={it.id}
              label={itemLabel(it)}
              carrier={it.tracking_carrier}
              number={it.tracking_number}
              eta={it.tracking_eta}
            />
          ))}

          {(req.notes || itemNotes.length > 0) && (
            <>
              <SubHead>Requester notes</SubHead>
              {req.notes && (
                <div style={{ background: '#F5F5F3', borderRadius: 8, padding: '10px 12px', fontSize: 12.5, color: '#3F3F3F', marginBottom: 7 }}>
                  {req.notes}
                </div>
              )}
              {itemNotes.map(it => (
                <div key={it.id} style={{ background: '#F5F5F3', borderRadius: 8, padding: '10px 12px', fontSize: 12.5, color: '#3F3F3F', marginBottom: 7 }}>
                  <b style={{ color: '#0F0F0F' }}>{itemLabel(it)}:</b> {it.notes}
                </div>
              ))}
            </>
          )}

          {/* #289 interaction, and it is the reason canSeeVendorContact exists.
              This card used to render behind canActionVendorCatalog, so taking
              Bridgette's vendor-catalog capability away — the entire point of
              #289 — would have removed the vendor's phone and email from the one
              surface #295 exists to put them on. Seeing a supplier's number is
              not maintaining supplier master data, and SAP separates vendor
              display from vendor maintain for exactly this reason. */}
          {canSeeContact && (
            <>
              <SubHead>Vendor contact</SubHead>
              <VendorContactCard vendor={vendor} showName />
            </>
          )}
        </div>
      )}
    </div>
  );
}

function SubHead({ children }) {
  return (
    <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase', color: '#7A7A7A', margin: '14px 0 7px' }}>
      {children}
    </div>
  );
}

function TrackRow({ label, carrier, number, eta }) {
  return (
    <div style={{ border: '1px solid #E8E8E8', borderRadius: 7, padding: '9px 11px', marginBottom: 7 }}>
      <div style={{ fontSize: 12.5, fontWeight: 600 }}>{label}</div>
      <div style={{ fontSize: 11, color: '#7A7A7A', marginTop: 2, display: 'flex', gap: 7, flexWrap: 'wrap', alignItems: 'center' }}>
        <span>
          {carrier ? carrier + ' · ' : ''}
          {number || 'No tracking number'}
        </span>
        {eta && (
          <span style={{ fontSize: 10, background: '#E6F1FB', color: '#0C447C', borderRadius: 4, padding: '1px 6px', fontWeight: 600 }}>
            ETA {fmtDate(eta)}
          </span>
        )}
      </div>
    </div>
  );
}

function InvoiceDetailBody({ req, branchName, onEnterInvoice, canReconcile, canSeeContact, closeProps, resolveProps, creditProps, qbProps }) {
  const items = req.material_request_items || [];
  const ready = hasInvoiceableLines(req);
  const total = orderedTotalOf(req);
  const fields = [
    ['PO number', req.po_ref || '—'],
    ['Work order', req.work_order_ref || '—'],
    ['Vendor', req.inventory_vendors?.name || '—'],
    ['Ordered total', total != null ? money(total) : '—'],
  ];

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 9, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 10, color: '#9A9A9A', letterSpacing: 0.4 }}>{req.request_number}</span>
        <StatusBadges req={req} />
      </div>
      <div style={{ fontSize: 19, fontWeight: 700, margin: '5px 0 2px' }}>{requestHeadline(req)}</div>
      <div style={{ fontSize: 11.5, color: '#5C5C5C' }}>
        {branchName} branch
        {req.ordered_at ? ' · ordered ' + fmtDate(req.ordered_at) : ''}
        {req.users?.full_name ? ' · requested by ' + req.users.full_name : ''}
      </div>

      {/* Decision 4 at the 74y gate: AP gets read-only visibility into the
          purchase order rather than concealment. Oracle and Coupa both do this —
          it is what makes a three-way match reviewable rather than merely
          computed. Said on screen because a surface that silently cannot be
          edited reads as a bug. */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8, background: '#F7F7F6',
        border: '1px solid #E8E8E8', borderLeft: '3px solid #9A9A9A',
        borderRadius: '0 7px 7px 0', padding: '9px 12px', fontSize: 12, color: '#5C5C5C', margin: '14px 0',
      }}>
        <IconLock size={14} style={{ flexShrink: 0 }} />
        <span><b style={{ color: '#0F0F0F' }}>Read-only.</b> Order and receipt data belongs to Purchasing. You can see it; you cannot change it.</span>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px 26px', margin: '16px 0 20px' }}>
        {fields.map(([k, v]) => (
          <div key={k}>
            <div style={{ fontSize: 10, letterSpacing: 1, textTransform: 'uppercase', color: '#9A9A9A', fontWeight: 700 }}>{k}</div>
            <div style={{ fontSize: 14.5, fontWeight: 600, marginTop: 3, fontVariantNumeric: 'tabular-nums' }}>{v}</div>
          </div>
        ))}
      </div>

      {/* #545/D13/D15, Session 79.14d. The vendor's order number used to be a
          lone line here that rendered ONLY when present — so the panel changed
          shape between two orders, which is exactly what reads as a rendering
          fault rather than as a fact. It is in the block below with the other
          five, present or absent.

          This page was already the best of the five surfaces and still showed
          only two of the six: it is the one screen that displayed what it
          searched. The gap it had was the empty state. */}
      {/* No lead-chip highlight here, deliberately. This body sits two
          presentational components below the page that owns the search box, and
          threading a search term through two layers whose whole job is to be
          dumb costs more than the highlight is worth. D13's requirement is that
          the record SHOWS the numbers, which it now does; marking which one you
          typed is a nicety and it is kept where the search is new — the
          Purchasing Queue. */}
      <OrderIdentifiers req={req} style={{ marginTop: -6, marginBottom: 16 }} />

      {/* #413, Session 78n8.3 — Colby's addition to the issue, and it completes
          decision 4 of the 74y gate rather than extending it. That decision
          already ruled that AP gets read-only visibility into the purchase order
          "rather than concealment", because it is what makes a three-way match
          reviewable instead of merely computed — the read-only banner above says
          so. Until now the only thing AP could see was the DATA about the order;
          the document D&R actually sent the vendor could not be produced at all.

          This is the surface with the strongest claim of the three, because
          Invoicing already searches by po_ref: a clerk arrives here holding the
          vendor's bill and the exact reference the document is filed under, and
          the question they are asking — does this bill match what we ordered —
          is answered by the piece of paper the vendor was sent. */}
      <div style={{ marginTop: -6, marginBottom: 16 }}>
        <PurchaseOrderDownload poDocument={req.po_document} label="Download the PO we sent" />
      </div>

      {items.length === 0
        ? <div style={{ fontSize: 12, color: '#9A9A9A' }}>This request has no line items.</div>
        : <LineTable req={req} {...closeProps} />}

      <InvoiceHistory
        req={req}
        canResolve={canReconcile}
        onResolve={inv => resolveProps?.onResolve?.(req, inv)}
        onCredit={inv => creditProps?.onCredit?.(req, inv)}
        onSendToQb={qbProps?.onSend ? inv => qbProps.onSend(req, inv) : null}
      />

      <FullOrderDetail req={req} canSeeContact={canSeeContact} />

      {/* #295. "View full order" is gone rather than deep-linked. The section
          above carries what it promised, so there is no navigation to fix and no
          return path to invent — this application has no navigation history
          anywhere, and building one for a single link was the alternative. */}
      {ready && canReconcile && (
        <div style={{ display: 'flex', gap: 9, marginTop: 16, flexWrap: 'wrap' }}>
          <button onClick={() => onEnterInvoice(req)}
            style={{ height: 38, padding: '0 16px', borderRadius: 8, fontSize: 13.5, fontWeight: 600, border: 'none', background: MAGENTA, color: 'white', cursor: 'pointer' }}>
            Enter invoice
          </button>
        </div>
      )}
    </>
  );
}

function InvoiceDetailPanel({ req, branchName, onEnterInvoice, canReconcile, canSeeContact, closeProps, resolveProps, creditProps, qbProps, emptyHint }) {
  if (!req) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: '#9A9A9A' }}>
        <IconFileInvoice size={28} style={{ opacity: 0.4 }} />
        {/* #298, 78n9.4. This said "Select an order to reconcile" on every tab,
            including the Unexported one — where a row click ticks a checkbox and
            opens nothing. An instruction that cannot be followed reads as a
            broken panel, which is #425's lesson at a smaller scale. */}
        <div style={{ fontSize: 13, marginTop: 10 }}>{emptyHint || 'Select an order to reconcile'}</div>
      </div>
    );
  }
  return (
    <div style={{ padding: '20px 22px 60px' }}>
      <InvoiceDetailBody req={req} branchName={branchName} onEnterInvoice={onEnterInvoice} canReconcile={canReconcile} canSeeContact={canSeeContact} closeProps={closeProps} resolveProps={resolveProps} creditProps={creditProps} qbProps={qbProps} />
    </div>
  );
}

function MobileInvoiceDetailSheet({ req, branchName, onClose, onEnterInvoice, canReconcile, canSeeContact, closeProps, resolveProps, creditProps, qbProps }) {
  if (!req) return null;
  return (
    <Overlay variant="sheet" onClose={onClose}>
      <div style={{ display: 'flex', flexDirection: 'column', maxHeight: '88vh' }}>
        <div style={{ width: 36, height: 4, borderRadius: 2, background: '#D0D0D0', margin: '12px auto 0', flexShrink: 0 }} />
        <div style={{ padding: '14px 18px 28px', overflowY: 'auto' }}>
          <InvoiceDetailBody req={req} branchName={branchName} onEnterInvoice={onEnterInvoice} canReconcile={canReconcile} canSeeContact={canSeeContact} closeProps={closeProps} resolveProps={resolveProps} creditProps={creditProps} qbProps={qbProps} />
        </div>
      </div>
    </Overlay>
  );
}

// Moved wholesale from PurchasingQueuePage.js at 74z, unchanged in behaviour.
// #290's per-vendor duplicate warning lands on this sheet at 74z2; the field it
// attaches to is the invoice number below.
// #343, Session 78m. D9 -- the entry's sharpest sentence is that the wrong number
// is invisible, so a correction that is equally invisible fixes the arithmetic and
// not the trust problem. This is the half-second where Bridgette learns that what
// she just typed changed the value of the shelf, stated in units and dollars
// rather than in column names.
//
// The wording is extracted from the component and exported so it
// can be tested without rendering anything. That is not tidiness: the first
// version of this text printed a NEGATIVE variance as a positive number, and it
// took entering a real invoice to notice. Four branches of prose deciding whether
// D&R was overcharged or undercharged deserve a test that does not need a browser.
//
// Returns null when there is nothing honest to say -- no payload (a database
// without the migration), mode 'off' (invoice prices reach nothing, by
// configuration), or nothing settled (every line off-catalog, or billed with no
// quantity). Silence is the correct output for all three.
export function costBasisSummaryText(cost) {
  if (!cost || cost.mode === 'off' || cost.linesSettled === 0) return null;

  const qty = n => (Number.isInteger(n) ? String(n) : String(Math.round(n * 100) / 100));
  const used = Math.max(cost.qtySettled - cost.qtyOnHand, 0);
  const hasVariance = Math.abs(cost.variance) >= 0.005;

  // DIRECTION IS NOT DECORATION. A variance is signed: positive means the invoice
  // came in ABOVE what the receipt had assumed, negative means below. Printing the
  // magnitude alone makes "we were overcharged $2,081" and "we were undercharged
  // $2,081" the same sentence, which is the one mistake a money screen must not
  // make. Caught by live test at 78m, not by review.
  const over = cost.variance > 0;
  const amount = money(Math.abs(cost.variance));

  // "What the receipt had assumed" rather than "what was expected", deliberately.
  // Two different comparisons now land on this screen and they must not blur:
  // the DISCREPANCY flag compares the invoice against the ORDERED price, while
  // this compares it against the cost the RECEIPT stamped. Saying "expected" for
  // both would leave Bridgette unable to tell which number moved.
  const versus = ' than the receipt had assumed';

  let title;
  let body;

  if (cost.mode === 'revalue_on_hand') {
    title = 'Cost basis updated';
    body = cost.qtyOnHand > 0
      ? qty(cost.qtyOnHand) + ' of ' + qty(cost.qtySettled) + ' still on hand — shelf value updated'
        + (used > 0 && hasVariance
            ? '. The other ' + qty(used) + ' had already been used, so ' + amount + ' '
              + (over ? 'more' : 'less') + versus + ' is recorded as a variance.'
            : '.')
      : 'All ' + qty(cost.qtySettled) + ' had already been used, so nothing on the shelf changed. '
        + amount + ' ' + (over ? 'more' : 'less') + versus + ' is recorded as a variance.';
  } else {
    title = hasVariance ? 'Cost basis · gap recorded' : 'Cost basis · no gap';
    body = hasVariance
      ? qty(cost.qtySettled) + ' units settled at the invoiced price, and shelf values were left alone. '
        + 'The invoice came in ' + amount + ' ' + (over ? 'above' : 'below') + versus
        + ', and that gap is now recorded.'
      : qty(cost.qtySettled) + ' units settled at the invoiced price, which matched what the receipt '
        + 'had assumed. Nothing to record.';
  }

  return { title, body };
}

// Module level, not nested inside EnterInvoiceSheet -- locked rule 2.
function CostBasisSummary({ cost }) {
  const text = costBasisSummaryText(cost);
  if (!text) return null;
  const { title, body } = text;

  return (
    <div style={{
      background: '#F6F4F8', border: '1px solid #E2DCE8', borderLeft: '3px solid ' + MAGENTA,
      borderRadius: '0 10px 10px 0', padding: '11px 13px', marginBottom: 16,
    }}>
      <div style={{ fontSize: 11.5, fontWeight: 700, color: '#4A3C52', marginBottom: 3 }}>{title}</div>
      <div style={{ fontSize: 11.5, color: '#5C5262', lineHeight: 1.55 }}>{body}</div>
    </div>
  );
}

function EnterInvoiceSheet({ visible, req, isMobile, invoiceItemPrices, setInvoiceItemPrice, invoiceRef, setInvoiceRef, onInvoiceRefBlur, dupeMatch, dupeChecking, invoiceDate, setInvoiceDate, invoiceTerms, setInvoiceTerms, invoiceDueDate, setInvoiceDueDate, invoiceTax, setInvoiceTax, invoiceSaving, invoiceError, invoiceDone, invoiceFlagged, invoiceCost, onSubmit, onClose }) {
  if (!visible || !req) return null;

  const items = req.material_request_items || [];
  const rows = invoiceableItems(items);
  // #283. Lines that have not arrived are shown, greyed, rather than dropped off
  // the sheet. They are still not billable — that is #279's rule and it stands —
  // but a total that is short of the invoice in Bridgette's hand with nothing on
  // screen explaining why is how a correct control gets worked around.
  const notYetReceived = items.filter(it => !rows.includes(it));
  const settledCount = items.filter(itemReceiptSettled).length;
  const isPartial = settledCount < items.length;

  const subtotal = rows.reduce((sum, it) => {
    const v = parseFloat(invoiceItemPrices[it.id]);
    return sum + (Number.isNaN(v) ? 0 : v);
  }, 0);
  const taxNum = invoiceTax === '' ? 0 : parseFloat(invoiceTax);
  const invoiceTotal = subtotal + (Number.isNaN(taxNum) ? 0 : taxNum);

  const headline = requestHeadline(req);

  return (
    <Overlay variant={isMobile ? 'sheet' : 'modal'} maxWidth={560} onClose={invoiceSaving ? undefined : onClose}>
      <div style={{ display: 'flex', flexDirection: 'column', maxHeight: '85vh' }}>
        {isMobile && <div style={{ width: 36, height: 4, borderRadius: 2, background: '#D0D0D0', margin: '12px auto 0', flexShrink: 0 }} />}

        <div style={{ background: '#0F0F0F', padding: '14px 20px', flexShrink: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: 'white' }}>Enter invoice</div>
          <div style={{ fontSize: 11, color: '#9A9A9A', marginTop: 2 }}>{req.request_number} · {headline}</div>
        </div>

        {invoiceDone ? (
          <div style={{ padding: '20px 20px 32px', overflowY: 'auto' }}>
            {invoiceFlagged ? (
              <div style={{ background: '#FFEBEE', borderRadius: 12, padding: 20, border: '1px solid #F09595', textAlign: 'center', marginBottom: 16 }}>
                <div style={{ fontSize: 15, fontWeight: 700, color: '#791F1F', marginBottom: 4 }}>Invoice saved · discrepancy flagged</div>
                <div style={{ fontSize: 12, color: '#791F1F' }}>Admin and executive have been notified.</div>
              </div>
            ) : (
              <div style={{ background: '#E8F5E9', borderRadius: 12, padding: 20, border: '1px solid #C8E6C9', textAlign: 'center', marginBottom: 16 }}>
                <div style={{ fontSize: 28, marginBottom: 6 }}>✓</div>
                <div style={{ fontSize: 15, fontWeight: 700, color: '#2E7D32', marginBottom: 4 }}>Invoice saved</div>
              </div>
            )}
            <CostBasisSummary cost={invoiceCost} />
            <button onClick={onClose} style={{ width: '100%', padding: 14, borderRadius: 12, fontSize: 14, fontWeight: 700, border: 'none', background: MAGENTA, color: 'white', cursor: 'pointer' }}>Done</button>
          </div>
        ) : (
          <div style={{ padding: '16px 20px 32px', overflowY: 'auto' }}>
            {invoiceError && <div style={{ background: '#FFEBEE', color: '#A32D2D', borderRadius: 8, padding: '8px 12px', fontSize: 12, marginBottom: 12 }}>{invoiceError}</div>}

            {isPartial && rows.length > 0 && (
              <div style={{ background: '#FFF8EC', border: '1px solid #F5D9A8', borderRadius: 10, padding: '9px 12px', marginBottom: 16, display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                <IconAlertTriangle size={14} color="#9A5F00" style={{ flexShrink: 0, marginTop: 1 }} />
                <span style={{ fontSize: 11.5, color: '#854F0B', lineHeight: 1.5 }}>
                  Not everything on this order has arrived. Billing what has arrived leaves the order open for the rest.
                </span>
              </div>
            )}

            <div style={{ fontSize: 10, fontWeight: 700, color: '#7A7A7A', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 8 }}>Invoice</div>

            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1.3fr 1fr', gap: 10, marginBottom: 10 }}>
              <div>
                <div style={{ fontSize: 9, fontWeight: 700, color: '#7A7A7A', textTransform: 'uppercase', marginBottom: 3 }}>
                  Invoice # <span style={{ color: '#A32D2D' }}>*</span>
                </div>
                <input type="text" value={invoiceRef} onChange={e => setInvoiceRef(e.target.value)} onBlur={onInvoiceRefBlur} placeholder="e.g. INV-88214"
                  style={{
                    width: '100%', height: 40, borderRadius: 8, padding: '0 12px', color: '#0F0F0F', boxSizing: 'border-box',
                    border: '1px solid ' + (dupeMatch ? '#E0A500' : '#E0E0E0'),
                    boxShadow: dupeMatch ? '0 0 0 2px rgba(224,165,0,.14)' : 'none',
                  }} />
              </div>
              <div>
                <div style={{ fontSize: 9, fontWeight: 700, color: '#7A7A7A', textTransform: 'uppercase', marginBottom: 3 }}>Invoice date</div>
                <input type="date" value={invoiceDate} onChange={e => setInvoiceDate(e.target.value)}
                  style={{ width: '100%', height: 40, border: '1px solid #E0E0E0', borderRadius: 8, padding: '0 10px', color: '#0F0F0F', boxSizing: 'border-box' }} />
              </div>
            </div>

            {/* #290. Names the colliding order rather than saying "this looks
                familiar", because the whole value of the warning is that
                Bridgette can go and check the other one. It does not block:
                one vendor invoice legitimately covering two orders is ordinary,
                and Oracle holds rather than rejects for the same reason. */}
            {dupeMatch && (
              <div style={{
                background: '#FFF8EC', border: '1px solid #F3D9A8', borderLeft: '3px solid #E0A500',
                borderRadius: '0 8px 8px 0', padding: '10px 12px', marginBottom: 12,
                display: 'flex', gap: 8, alignItems: 'flex-start',
              }}>
                <IconAlertTriangle size={14} color="#9A5F00" style={{ flexShrink: 0, marginTop: 1 }} />
                <div style={{ fontSize: 11.5, color: '#6B4A08', lineHeight: 1.55 }}>
                  <div style={{ fontWeight: 700, marginBottom: 2 }}>Already recorded for this vendor</div>
                  <div>
                    <b>{dupeMatch.invoice_ref}</b> was entered on{' '}
                    <b>{dupeMatch.material_requests?.request_number || 'another order'}</b>
                    {dupeMatch.invoice_date ? ' on ' + fmtDateOnly(dupeMatch.invoice_date) : ''}
                    {dupeMatch.total_amount != null ? ', for ' + money(dupeMatch.total_amount) : ''}.
                  </div>
                  <div style={{ marginTop: 5 }}>
                    If this invoice covers both orders, carry on. If not, check it before saving.
                  </div>
                </div>
              </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : '1.3fr 1fr', gap: 10, marginBottom: 18 }}>
              <div>
                <div style={{ fontSize: 9, fontWeight: 700, color: '#7A7A7A', textTransform: 'uppercase', marginBottom: 3 }}>Terms</div>
                <select value={invoiceTerms} onChange={e => setInvoiceTerms(e.target.value)}
                  style={{ width: '100%', height: 40, border: '1px solid #E0E0E0', borderRadius: 8, padding: '0 8px', color: '#0F0F0F', background: 'white', boxSizing: 'border-box' }}>
                  {PAYMENT_TERMS.map(t => <option key={t.label} value={t.label}>{t.label}</option>)}
                </select>
              </div>
              <div>
                <div style={{ fontSize: 9, fontWeight: 700, color: '#7A7A7A', textTransform: 'uppercase', marginBottom: 3 }}>Due date</div>
                <input type="date" value={invoiceDueDate} onChange={e => setInvoiceDueDate(e.target.value)}
                  style={{ width: '100%', height: 40, border: '1px solid #E0E0E0', borderRadius: 8, padding: '0 10px', color: '#0F0F0F', boxSizing: 'border-box' }} />
              </div>
            </div>

            <div style={{ fontSize: 10, fontWeight: 700, color: '#7A7A7A', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 8 }}>
              {isPartial ? 'Lines received, not yet billed' : 'Lines to bill'}
            </div>

            {rows.length === 0 ? (
              <div style={{ background: '#F9F9F7', border: '1px solid #E8E8E8', borderRadius: 8, padding: '16px 12px', fontSize: 12, color: '#7A7A7A', textAlign: 'center', marginBottom: 16 }}>
                No received quantities are recorded on this request, so there is nothing to reconcile.
              </div>
            ) : rows.map(it => {
              const ordered = itemOrderedPrice(req, it, items.length);
              const entered = invoiceItemPrices[it.id] ?? '';
              const enteredNum = entered === '' ? null : parseFloat(entered);
              const differs = ordered != null && enteredNum != null && !Number.isNaN(enteredNum)
                && Math.abs(enteredNum - ordered) > 0.005;

              return (
                <div key={it.id} style={{ border: '1px solid #E8E8E8', borderRadius: 10, marginBottom: 10, overflow: 'hidden' }}>
                  <div style={{ padding: '10px 12px 12px' }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#0F0F0F', marginBottom: 8 }}>{itemLabel(it)}</div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1.2fr', gap: 8, alignItems: 'end' }}>
                      <div>
                        <div style={{ fontSize: 9, fontWeight: 700, color: '#7A7A7A', textTransform: 'uppercase', marginBottom: 3 }}>Ordered price</div>
                        <div style={{ background: '#F9F9F7', border: '1px solid #E8E8E8', borderRadius: 8, height: 40, display: 'flex', alignItems: 'center', padding: '0 10px', boxSizing: 'border-box' }}>
                          <span style={{ fontSize: 13, fontWeight: 700, color: ordered != null ? '#0F0F0F' : '#B0B0B0', fontStyle: ordered != null ? 'normal' : 'italic' }}>
                            {ordered != null ? money(ordered) : 'Unpriced'}
                          </span>
                        </div>
                      </div>

                      <div>
                        <div style={{ fontSize: 9, fontWeight: 700, color: '#7A7A7A', textTransform: 'uppercase', marginBottom: 3 }}>Qty to bill</div>
                        <div style={{ background: '#F9F9F7', border: '1px solid #E8E8E8', borderRadius: 8, height: 40, display: 'flex', alignItems: 'center', padding: '0 10px', boxSizing: 'border-box' }}>
                          <span style={{ fontSize: 13, fontWeight: 700, color: '#0F0F0F' }}>{itemOutstandingQty(it)} {itemUom(it)}</span>
                        </div>
                      </div>

                      <div>
                        <div style={{ fontSize: 9, fontWeight: 700, color: '#7A7A7A', textTransform: 'uppercase', marginBottom: 3 }}>Invoice price</div>
                        <div style={{ position: 'relative' }}>
                          <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', fontSize: 14, color: '#5C5C5C', pointerEvents: 'none' }}>$</span>
                          <input
                            type="number" step="0.01" inputMode="decimal"
                            value={entered}
                            onChange={e => setInvoiceItemPrice(it.id, e.target.value)}
                            onFocus={e => e.target.select()}
                            placeholder="0.00"
                            style={{ width: '100%', height: 40, border: '1px solid ' + (differs ? '#EF9F27' : '#E0E0E0'), borderRadius: 8, padding: '0 10px 0 22px', color: '#0F0F0F', boxSizing: 'border-box' }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {differs && (
                    <div style={{ background: '#FFF8EC', borderTop: '1px solid #F5D9A8', padding: '7px 12px', display: 'flex', alignItems: 'center', gap: 6 }}>
                      <IconAlertTriangle size={13} color="#9A5F00" style={{ flexShrink: 0 }} />
                      <span style={{ fontSize: 11, fontWeight: 600, color: '#9A5F00' }}>Differs from ordered price</span>
                    </div>
                  )}
                </div>
              );
            })}

            {notYetReceived.map(it => (
              <div key={it.id} style={{ background: '#F9F9F7', border: '1px solid #E8E8E8', borderRadius: 10, padding: '10px 12px', marginBottom: 10, display: 'flex', gap: 8, alignItems: 'center' }}>
                <IconClock size={14} color="#9A9A9A" style={{ flexShrink: 0 }} />
                <span style={{ fontSize: 11.5, color: '#7A7A7A' }}>
                  {itemLabel(it)} — {itemBilledQty(it) > 0 ? 'already billed in full' : 'not yet received, so not billable'}
                </span>
              </div>
            ))}

            <div style={{ borderTop: '1px solid #E8E8E8', paddingTop: 12, marginTop: 14 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <span style={{ fontSize: 12, color: '#5C5C5C' }}>Subtotal</span>
                <span style={{ fontSize: 13, fontWeight: 700, color: '#0F0F0F' }}>{money(subtotal)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                <span style={{ fontSize: 12, color: '#5C5C5C' }}>Sales tax <span style={{ color: '#9A9A9A' }}>(optional)</span></span>
                <div style={{ position: 'relative', width: 110 }}>
                  <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', fontSize: 14, color: '#5C5C5C', pointerEvents: 'none' }}>$</span>
                  <input type="number" step="0.01" inputMode="decimal" value={invoiceTax}
                    onChange={e => setInvoiceTax(e.target.value)} onFocus={e => e.target.select()} placeholder="0.00"
                    style={{ width: '100%', height: 36, border: '1px solid #E0E0E0', borderRadius: 8, padding: '0 10px 0 22px', color: '#0F0F0F', boxSizing: 'border-box' }} />
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: 10, borderTop: '1px solid #E8E8E8', marginBottom: 18 }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: '#0F0F0F' }}>Invoice total</span>
                <span style={{ fontSize: 16, fontWeight: 700, color: '#0F0F0F' }}>{money(invoiceTotal)}</span>
              </div>
            </div>

            {/* #290. The label changes once a collision has been surfaced, so the
                second press is a deliberate act rather than the same button
                pressed twice. Disabled while the check is in flight — pressing
                Confirm before the answer arrives is the one case that could
                still slip past. */}
            <button onClick={onSubmit} disabled={invoiceSaving || dupeChecking || rows.length === 0}
              style={{ width: '100%', padding: 14, borderRadius: 12, fontSize: 14, fontWeight: 700, border: 'none', background: (invoiceSaving || dupeChecking || rows.length === 0) ? '#E0E0E0' : (dupeMatch ? '#9A5F00' : MAGENTA), color: (invoiceSaving || dupeChecking || rows.length === 0) ? '#9A9A9A' : 'white', cursor: (invoiceSaving || dupeChecking || rows.length === 0) ? 'not-allowed' : 'pointer', marginBottom: 8 }}>
              {invoiceSaving ? 'Saving...' : dupeChecking ? 'Checking invoice number...' : dupeMatch ? 'Save anyway' : 'Confirm'}
            </button>
            <button onClick={onClose} style={{ width: '100%', background: 'none', border: 'none', fontSize: 13, color: '#7A7A7A', cursor: 'pointer', padding: 4 }}>Cancel</button>
          </div>
        )}
      </div>
    </Overlay>
  );
}

// ── PAGE ─────────────────────────────────────────────────────────────────────

export default function InvoicingPage({ onCountsChanged }) {
  const { profile, isAdmin, isManager, isExecutive, isPurchasing } = useAuth();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  const [requests, setRequests] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('awaiting');
  const [search, setSearch] = useState('');
  const [selectedReq, setSelectedReq] = useState(null);
  const [mobileDetailReq, setMobileDetailReq] = useState(null);

  const [showEnterInvoice, setShowEnterInvoice] = useState(false);
  const [invoiceReq, setInvoiceReq] = useState(null);
  const [invoiceItemPrices, setInvoiceItemPrices] = useState({});
  const [invoiceRef, setInvoiceRef] = useState('');
  const [invoiceDate, setInvoiceDate] = useState('');
  const [invoiceTerms, setInvoiceTerms] = useState('');
  const [invoiceDueDate, setInvoiceDueDate] = useState('');
  const [invoiceTax, setInvoiceTax] = useState('');
  const [invoiceSaving, setInvoiceSaving] = useState(false);
  const [invoiceError, setInvoiceError] = useState('');
  const [invoiceDone, setInvoiceDone] = useState(false);
  const [invoiceFlagged, setInvoiceFlagged] = useState(false);
  // #343, Session 78m. What the save just did to the cost basis, straight from
  // enter_material_request_invoice()'s return payload rather than re-queried.
  // Null until a save has run; stays null on a database that has not had
  // 20260829_cost_basis_correction.sql applied, which is what makes the
  // confirmation block below degrade to exactly today's screen.
  const [invoiceCost, setInvoiceCost] = useState(null);
  // #290, Session 74z2. Null until a check has run and found a collision; then
  // the colliding invoice's own row, so the warning can name the order rather
  // than saying "this looks familiar".
  const [dupeMatch, setDupeMatch] = useState(null);
  const [dupeChecking, setDupeChecking] = useState(false);
  // Set once the warning has been shown for the current number, so a second
  // press of Save goes through. This is what makes the control unraceable: the
  // blur check is a courtesy, the submit check is the gate.
  const [dupeAcknowledged, setDupeAcknowledged] = useState(false);

  // #288, Session 74z3 — the invoice-close half. AP is the one holding the
  // invoice, so AP is the one who knows the vendor has billed for the last
  // time. SAP sets its final-invoice indicator the same way, during invoice
  // verification, and never automatically.
  const [shortCloseReq, setShortCloseReq] = useState(null);
  const [shortCloseItem, setShortCloseItem] = useState(null);
  const [shortCloseSaving, setShortCloseSaving] = useState(false);
  const [shortCloseError, setShortCloseError] = useState('');
  const [shortCloseDone, setShortCloseDone] = useState(false);
  const [shortCloseResult, setShortCloseResult] = useState(null);

  // #301, Session 74z4.
  const [resolveReq, setResolveReq] = useState(null);
  const [resolveInvoice, setResolveInvoice] = useState(null);
  const [resolveSaving, setResolveSaving] = useState(false);
  const [resolveError, setResolveError] = useState('');
  const [resolveDone, setResolveDone] = useState(false);
  const [resolveResult, setResolveResult] = useState(null);

  // #299, Session 74z5. The credit flow. creditLines is fetched per invoice when
  // the sheet opens rather than embedded in load()'s select: the sheet needs one
  // invoice's lines, the list query would have carried every line of every
  // invoice on every request on every page load, and material_request_invoice_lines
  // sits two hops from material_requests — territory where PostgREST's nested
  // behaviour is worth staying out of.
  const [creditReq, setCreditReq] = useState(null);
  const [creditInvoice, setCreditInvoice] = useState(null);
  const [creditLines, setCreditLines] = useState([]);
  const [creditLinesLoading, setCreditLinesLoading] = useState(false);
  const [creditSaving, setCreditSaving] = useState(false);
  const [creditError, setCreditError] = useState('');
  const [creditDone, setCreditDone] = useState(false);
  const [creditResult, setCreditResult] = useState(null);

  // #298, 78n9.3. One invoice at a time — the batch send is gate screen 3 and
  // is deliberately not part of this.
  const [qbInvoice, setQbInvoice] = useState(null);

  // #298, 78n9.4. Gate screen 3. Invoice ids, as a Set rather than an array:
  // the toggle and the checked test both run per row on every render, and a Set
  // keeps them O(1) on a tab that legitimately holds every unpaid invoice.
  const [selectedInvoiceIds, setSelectedInvoiceIds] = useState(() => new Set());
  const [showBatchSheet, setShowBatchSheet] = useState(false);
  const [batchNote, setBatchNote] = useState('');

  const canReconcile = canReconcileInvoice({ isAdmin, isManager, isExecutive, isPurchasing, profile });
  // Callouts gate Wave 2 — inq_tabs_first. Accounts payable, which is what
  // canReconcileInvoice already means on this screen.
  const discrepancyTabRef = useRef(null);
  // Its own predicate rather than reusing canReconcile at the call site, so the
  // question "may this person declare billing finished" can move independently
  // of "may this person enter an invoice" — the reason canSeeInvoicingNav is
  // also its own name.
  const canShortClose = canCloseLineForBilling({ isAdmin, isManager, isExecutive, isPurchasing, profile });
  // #295 + #289. Deliberately canSeeVendorContact rather than canContactVendor:
  // the team-member branch of canContactVendor is unreachable on this page,
  // which is gated by canReconcileInvoice, so requiring company settings here
  // would be loading a value that can never change the answer.
  const canSeeContact = canSeeVendorContact({ isAdmin, isManager, isExecutive, isPurchasing, profile });

  // Branch names come from the `branches` table rather than a hardcoded array.
  // #252 already counts thirteen files that hardcode the four-branch list; this
  // page deliberately does not become the fourteenth, and one hop through an FK
  // PostgREST resolves costs nothing.
  useEffect(() => {
    supabase.from('branches').select('id, name').then(({ data }) => setBranches(data || []));
  }, []);

  const branchName = useCallback(
    id => branches.find(b => String(b.id) === String(id))?.name || '—',
    [branches]
  );

  // Deliberately not branch-scoped, and this is the same reasoning that keeps
  // PurchasingQueuePage.js unscoped: purchasing spans branches by design, and
  // accounts payable spans them harder — Bridgette pays every branch's invoices.
  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data, error: err } = await supabase
        .from('material_requests')
        .select(`
          id, request_number, branch_id, status, created_at, updated_at, ordered_at,
          work_order_ref, po_ref, vendor_order_ref, vendor_id, ordered_price, po_document,
          invoice_ref,
          requested_by, notes,
          tracking_carrier, tracking_number, tracking_eta,
          inventory_vendors ( id, name, contact_name, phone, email ),
          material_request_items (
            id, item_id, qty_requested, qty_received, notes,
            is_off_catalog, off_catalog_description, off_catalog_uom,
            price_per_pack, packs_ordered, ordered_price,
            receipt_closed, receipt_closed_at, receipt_close_reason,
            invoice_closed, invoice_closed_at, invoice_close_reason,
            tracking_carrier, tracking_number, tracking_eta,
            receipt_closed_by_user:users!material_request_items_receipt_closed_by_fkey ( id, full_name ),
            invoice_closed_by_user:users!material_request_items_invoice_closed_by_fkey ( id, full_name ),
            material_request_invoice_lines ( id, invoice_id, qty_invoiced, line_amount ),
            inventory_items ( id, name, sku, uom )
          ),
          material_request_invoices (
            id, invoice_ref, invoice_date, due_date, payment_terms,
            document_type, credit_of_invoice_id,
            quickbooks_bill_id, exported_at, exported_by_name_snapshot,
            subtotal, tax_amount, total_amount, discrepancy_flagged, discrepancy_amount, entered_at,
            discrepancy_resolved, discrepancy_resolved_at, discrepancy_resolution, discrepancy_resolution_reason,
            discrepancy_resolved_by_user:users!material_request_invoices_discrepancy_resolved_by_fkey ( id, full_name )
          ),
          users!material_requests_requested_by_fkey ( id, full_name )
        `)
        .in('status', ['ordered', 'received', 'closed'])
        .order('updated_at', { ascending: false })
        .order('id', { foreignTable: 'material_request_items', ascending: true });

      if (err) throw err;
      setRequests(data || []);
    } catch (e) {
      setError('Failed to load invoicing: ' + e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  function setInvoiceItemPrice(itemId, value) {
    setInvoiceItemPrices(prev => ({ ...prev, [itemId]: value }));
  }

  // #285. Terms and due date move together: changing either keeps the pair
  // consistent, and editing the due date directly wins, because a vendor's
  // stated due date beats a computed one.
  function setInvoiceTermsAndDue(terms) {
    setInvoiceTerms(terms);
    setInvoiceDueDate(dueDateFromTerms(invoiceDate, terms));
  }

  function setInvoiceDateAndDue(date) {
    setInvoiceDate(date);
    setInvoiceDueDate(dueDateFromTerms(date, invoiceTerms));
  }

  function openEnterInvoice(req) {
    const today = todayInputValue();
    setInvoiceReq(req);
    setInvoiceItemPrices({});
    setInvoiceRef('');
    setInvoiceDate(today);
    setInvoiceTerms('Net 30');
    setInvoiceDueDate(dueDateFromTerms(today, 'Net 30'));
    setInvoiceTax('');
    setInvoiceDone(false);
    setInvoiceFlagged(false);
    setInvoiceCost(null);
    setInvoiceError('');
    setDupeMatch(null);
    setDupeAcknowledged(false);
    setShowEnterInvoice(true);
  }

  // #290, Session 74z2. Duplicate vendor invoice detection.
  //
  // Three-way matching is the standard defence against ghost invoices and
  // duplicate payments, which the AP literature puts at 1-3% of total spend.
  // Stede built the match at #191/#283 and left this out: the match asks whether
  // an invoice agrees with its order and its receipt, and says nothing about
  // whether that invoice has been seen before.
  //
  // A UNIQUE constraint is the obvious fix and it is wrong, for two ordinary
  // reasons: a vendor's invoice number is unique only within that vendor (two
  // suppliers can both issue 1001), and one vendor invoice can legitimately
  // cover two D&R orders. So this warns and lets the save through — Oracle's
  // hold-rather-than-reject shape — and names the colliding order so Bridgette
  // can go and look rather than guess.
  //
  // The vendor is NOT on the invoice table: material_request_invoices carries
  // request_id and no vendor column, so the vendor resolves one hop through
  // material_requests. This deliberately fetches by invoice_ref and filters the
  // vendor client-side rather than filtering on the nested relation, because
  // PostgREST returns empty silently on nested filters and a duplicate check
  // that silently finds nothing is worse than no check at all.
  // Returns the colliding row as well as setting it, because submitEnterInvoice
  // needs the answer synchronously rather than waiting for a state update.
  const checkDuplicateInvoiceRef = useCallback(async (ref, vendorId, selfRequestId) => {
    const trimmed = (ref || '').trim();
    if (!trimmed || vendorId == null) { setDupeMatch(null); return null; }
    setDupeChecking(true);
    try {
      const { data, error: dupeErr } = await supabase
        .from('material_request_invoices')
        .select('id, invoice_ref, invoice_date, total_amount, request_id, material_requests ( request_number, vendor_id )')
        .eq('invoice_ref', trimmed)
        .limit(50);
      if (dupeErr) throw dupeErr;
      const hit = (data || []).find(inv =>
        String(inv.material_requests?.vendor_id) === String(vendorId)
        && String(inv.request_id) !== String(selfRequestId)
      );
      setDupeMatch(hit || null);
      return hit || null;
    } catch (e) {
      // A failed check must not block entry or masquerade as "no duplicate".
      // It clears the match and stays quiet; the control is a warning, and a
      // warning that cannot run is not a reason to stop Bridgette working.
      setDupeMatch(null);
      return null;
    } finally {
      setDupeChecking(false);
    }
  }, []);

  function setInvoiceRefAndCheck(value) {
    setInvoiceRef(value);
    // Clear both: a stale warning against a number that has been edited is worse
    // than none, because it names an order that no longer collides — and an
    // acknowledgement carried over from a different number would wave the new
    // one straight through, which is the control failing silently.
    setDupeMatch(null);
    setDupeAcknowledged(false);
  }

  async function submitEnterInvoice() {
    if (!invoiceReq) return;

    const rows = invoiceableItems(invoiceReq.material_request_items || []);
    if (rows.length === 0) {
      setInvoiceError('No received quantities are recorded on this request, so there is nothing to reconcile.');
      return;
    }

    const payload = [];
    for (const it of rows) {
      const raw = invoiceItemPrices[it.id];
      if (raw == null || String(raw).trim() === '') {
        setInvoiceError('Enter an invoice price for every received item — pricing only some of them would understate the request total and flag a discrepancy that is not real.');
        return;
      }
      const num = parseFloat(raw);
      if (Number.isNaN(num) || num < 0) {
        setInvoiceError('"' + itemLabel(it) + '" has an invalid invoice price.');
        return;
      }
      // #283. qty_invoiced is derived, never typed — it is the quantity that has
      // arrived and not yet been billed, which is what the vendor is billing for
      // in every ordinary case. Recording it is what gives the three-way match
      // (#191) its data without costing Bridgette a keystroke, and it is what
      // the database checks against qty_received before accepting the line.
      payload.push({
        request_item_id: it.id,
        invoice_price: num,
        qty_invoiced: itemOutstandingQty(it),
      });
    }

    // #290, Session 74z2 — the invoice number is required, and it was optional.
    // Two reasons, and either alone would be enough. The duplicate check cannot
    // fire on a null, so leaving it optional ships a control that is switched
    // off exactly when someone is entering an invoice carelessly. And #298's
    // QuickBooks export needs a Bill No. on every row, so a null here becomes an
    // unimportable line later. SAP's MIRO makes the reference mandatory too.
    if (!invoiceRef.trim()) {
      setInvoiceError('Enter the vendor’s invoice number. It is what tells a repeat bill from a new one, and QuickBooks needs it on every bill.');
      return;
    }

    if (!invoiceDate) {
      setInvoiceError('Enter the date on the invoice — without it there is no aging or payment-terms tracking.');
      return;
    }

    const taxNum = invoiceTax === '' ? null : parseFloat(invoiceTax);
    if (taxNum != null && (Number.isNaN(taxNum) || taxNum < 0)) {
      setInvoiceError('Sales tax is not a valid amount.');
      return;
    }

    // #290. The gate, and it is here rather than on the field's blur handler for
    // a reason found while building it: clicking Save blurs the input, so the
    // blur check and the save race each other and the warning loses about as
    // often as it wins. Checking here cannot be raced — the first press surfaces
    // the collision and saves nothing, a second press goes through.
    if (!dupeAcknowledged) {
      const hit = await checkDuplicateInvoiceRef(invoiceRef, invoiceReq.vendor_id, invoiceReq.id);
      if (hit) {
        setDupeAcknowledged(true);
        setInvoiceError('');
        return;
      }
    }

    setInvoiceSaving(true);
    setInvoiceError('');
    try {
      const { data, error: rpcErr } = await supabase.rpc('enter_material_request_invoice', {
        p_request_id: invoiceReq.id,
        p_user_id: profile.id,
        p_items: payload,
        p_invoice_ref: invoiceRef.trim(),
        p_invoice_date: invoiceDate,
        p_due_date: invoiceDueDate || null,
        p_payment_terms: invoiceTerms || null,
        p_tax_amount: taxNum,
      });
      if (rpcErr) throw rpcErr;

      const flagged = !!data?.flagged;
      // #283. The comparison is no longer invoice total vs whole-order total.
      // The function returns the expected price of only the lines this invoice
      // finished, and the amount actually billed against those same lines,
      // pre-tax on both sides — so the notification quotes the figures that were
      // actually compared rather than two numbers that were never like for like.
      const orderedTotal = data?.expected_total != null ? parseFloat(data.expected_total) : null;
      // Not the invoice total: on a line finished across two invoices this is the
      // cumulative amount billed against it, which is the figure compared.
      const comparedTotal = data?.actual_total != null ? parseFloat(data.actual_total) : 0;

      if (flagged) {
        const { data: execUsers } = await supabase
          .from('users')
          .select('id')
          .in('role', ['admin', 'executive'])
          .eq('is_active', true);
        const execIds = (execUsers || []).map(u => u.id);

        const itemName = requestHeadline(invoiceReq);
        const orderedLabel = orderedTotal != null ? money(orderedTotal) : 'no recorded order price';

        if (execIds.length > 0) {
          await sendNotification(
            execIds,
            'Invoice price discrepancy',
            itemName + ' — ' + invoiceReq.request_number + ': billed ' + money(comparedTotal) + ' vs ordered ' + orderedLabel + '.',
            // #384, Session 79.12 -- WAS 'requests_inbox'. A reader told an
            // invoice was mispriced was sent to the Requests inbox, which
            // cannot show them the discrepancy or resolve it. Invoicing is the
            // screen with the Discrepancies tab on it, and the RESOLVED half of
            // this same lifecycle has always pointed there -- so the two ends of
            // one story disagreed about where the story lives.
            //
            // #315, Session 79.12 -- THE DOMAIN WAS 'inventory' AND A NOTIFICATION
            // ABOUT MONEY OWED TO A VENDOR WAS BADGED INVENTORY, IN GREEN.
            //
            // It could not be fixed by editing this one argument until today.
            // Topbar.js derived its chip from `event_type`, and `audit` is a
            // SEVERITY shared by eight writers across different domains -- so
            // re-tagging here would have corrected the two surfaces that read the
            // column and left the desktop bell disagreeing with both. #314's
            // structural half landed first and all three now read the column,
            // which is what makes this one word safe.
            //
            // The event type STAYS 'audit'. The orange rail is #246's
            // audit-severity treatment working correctly; the badge was the bug.
            'audit', INVOICING_DOMAIN, 'purchasing_invoicing', invoiceReq.id
          );
        }
        await notifyInvoiceDiscrepancy({
          requestNumber: invoiceReq.request_number,
          branchName: branchName(invoiceReq.branch_id),
          itemName,
          orderedPrice: orderedTotal ?? 0,
          invoicePrice: comparedTotal,
          invoiceRef: invoiceRef || null,
          // #126 — matches the sendNotification() directly above, which now points
          // at Invoicing. Gate decision G is that the email and the bell read the
          // same two columns, so the destination is wrong in both or right in
          // both. #384 found it wrong and CHANGED BOTH, in one commit: this call
          // and emailInvoiceDiscrepancy()'s ctaUrl in notify.js.
          requestId: invoiceReq.id,
        });
      }

      // #343. Read defensively rather than assumed: an unapplied migration
      // returns a payload with none of these keys, and the confirmation must
      // then show what it showed yesterday instead of "0 units settled".
      setInvoiceCost(
        data?.cost_basis_correction_mode
          ? {
              mode: data.cost_basis_correction_mode,
              linesSettled: Number(data.cost_lines_settled) || 0,
              qtySettled: Number(data.cost_qty_settled) || 0,
              qtyOnHand: Number(data.cost_qty_on_hand) || 0,
              variance: Number(data.cost_variance_total) || 0,
            }
          : null
      );

      setInvoiceFlagged(flagged);
      setInvoiceDone(true);
      await load();
      // The sidebar badge is owned by App.js, whose count effect only re-runs
      // when `active` changes - so saving an invoice left the badge reading 6
      // against a page reading 5 until the next navigation. Caught by live test
      // at 74z, and it is the same defect #243's off-catalog badge was given
      // onCountsChanged to fix at 74n. Same escape hatch, same reason.
      onCountsChanged?.();
    } catch (e) {
      setInvoiceError('Something went wrong: ' + e.message);
    } finally {
      setInvoiceSaving(false);
    }
  }

  // ── #288, Session 74z3: invoice close ──────────────────────────────────────
  //
  // Same RPC as the buyer's receipt close, called with the other flag. One
  // function rather than two because the settle rule that decides whether the
  // order is finished has to be identical from both sides — and because that
  // rule now lives in one place, shared with enter_material_request_invoice().
  function openShortClose(req, item) {
    setShortCloseReq(req);
    setShortCloseItem(item);
    setShortCloseError('');
    setShortCloseDone(false);
    setShortCloseResult(null);
  }

  function closeShortCloseSheet() {
    setShortCloseReq(null);
    setShortCloseItem(null);
    setShortCloseError('');
    setShortCloseDone(false);
    setShortCloseResult(null);
  }

  async function submitShortClose(reason) {
    if (!shortCloseItem) return;
    setShortCloseSaving(true);
    setShortCloseError('');
    try {
      const result = await setLineCloseFlag({
        itemId: shortCloseItem.id,
        userId: profile.id,
        flag: INVOICE,
        closed: true,
        reason,
      });
      setShortCloseResult(result);
      setShortCloseDone(true);
      await load();
      // Closing the last line settles the order, which moves it off Awaiting
      // invoice and changes the sidebar count. App.js owns that badge and only
      // recounts on navigation, so it needs telling — the same escape hatch
      // submitEnterInvoice uses, for the same reason.
      onCountsChanged?.();
    } catch (e) {
      setShortCloseError('Could not close the line: ' + e.message);
    } finally {
      setShortCloseSaving(false);
    }
  }

  // #301, Session 74z4 — the resolve flow.
  function closeResolveSheet() {
    setResolveReq(null);
    setResolveInvoice(null);
    setResolveError('');
    setResolveDone(false);
    setResolveResult(null);
  }

  // Opening on an already-resolved invoice is the reversal, and it is one press
  // with no sheet — an undo is not a decision, so it takes no outcome and no
  // reason. Safe to make cheap because the reversal writes its own audit row,
  // which is the same argument #288 made for its reopen.
  function openResolve(req, inv) {
    if (isResolved(inv)) {
      reopenDiscrepancy(req, inv);
      return;
    }
    setResolveReq(req);
    setResolveInvoice(inv);
    setResolveError('');
    setResolveDone(false);
    setResolveResult(null);
  }

  async function reopenDiscrepancy(req, inv) {
    try {
      await setInvoiceResolution({
        invoiceId: inv.id,
        userId: profile.id,
        resolved: false,
      });
      await load();
      onCountsChanged?.();
    } catch (e) {
      setError('Could not reopen the discrepancy: ' + e.message);
    }
  }

  async function submitResolve({ outcome, reason }) {
    if (!resolveInvoice) return;
    setResolveSaving(true);
    setResolveError('');
    try {
      const result = await setInvoiceResolution({
        invoiceId: resolveInvoice.id,
        userId: profile.id,
        resolved: true,
        outcome,
        reason,
      });
      setResolveResult(result);
      setResolveDone(true);

      // Oracle's hold-release shape, and decision 6's second event. Whoever was
      // told the discrepancy existed is told it ended — the flag notification at
      // submitEnterInvoice goes to admins and executives, so this closes the loop
      // with the same audience rather than a different one.
      //
      // Invoicing is its own notification family per #284's decision 1, so the
      // domain is new rather than a copy of purchasing's, and sendNotification
      // takes it as its FIFTH POSITIONAL argument.
      const { data: execUsers } = await supabase
        .from('users')
        .select('id')
        .in('role', ['admin', 'executive'])
        .eq('is_active', true);
      const execIds = (execUsers || []).map(u => u.id);
      if (execIds.length > 0) {
        const label = outcomeFor(outcome)?.label || outcome;
        await sendNotification(
          execIds,
          'Invoice discrepancy resolved',
          (resolveReq?.request_number || 'An order') + ' · ' +
            (resolveInvoice.invoice_ref || 'no invoice number') + ' — ' + label + '. ' + reason,
          EVENT_DISCREPANCY_RESOLVED, INVOICING_DOMAIN, 'purchasing_invoicing', resolveReq?.id ?? null
        );
      }

      await load();
      // Resolving the last open discrepancy takes the order off the Discrepancies
      // tab, which changes the sidebar count App.js owns.
      onCountsChanged?.();
    } catch (e) {
      setResolveError('Could not resolve the discrepancy: ' + e.message);
    } finally {
      setResolveSaving(false);
    }
  }

  // One press, no sheet, as approved at 74z3's gate. Safe to make that cheap
  // because the reversal writes its own audit row — an undo is as visible
  // afterwards as the close it undid.
  //
  // Unlike the buyer's surface, a settled order stays reachable here: this
  // page loads 'closed' as well as 'ordered' and 'received', so the Settled tab
  // still holds it and the reversal is one tab away rather than gone.
  async function reopenLineForBilling(item) {
    try {
      await setLineCloseFlag({
        itemId: item.id,
        userId: profile.id,
        flag: INVOICE,
        closed: false,
        reason: null,
      });
      await load();
      onCountsChanged?.();
    } catch (e) {
      setError('Could not reopen the line: ' + e.message);
    }
  }

  // ── Derived lists ──────────────────────────────────────────────────────────

  // #296, Session 74z2. An order that has been invoiced once, has nothing
  // billable *right now*, and is still waiting on the rest of its shipment used
  // to fall through all three tabs: not Awaiting (nothing billable), not a
  // Discrepancy (nothing flagged), not Settled (still `ordered`). It rendered
  // nowhere, and search could not reach it either.
  //
  // Two things this deliberately is NOT:
  //
  // It is not a fourth tab. That is a new UI surface on a three-tab shape
  // approved at 74y's gate, and neither SAP nor Coupa gives this state its own
  // destination — SAP's MIRO worklist keeps showing open items including
  // invoices already posted, and Coupa handles the equivalent as a view on one
  // table. Keeping it here is what those two converge on.
  //
  // It is also not "anything still outstanding", which was the phrasing the
  // issue entry reached for. That would pull in every ordered-but-not-yet-
  // received request and flood AP's tab with orders they cannot act on. The gap
  // is specifically money already in flight: at least one invoice recorded, and
  // more still to come.
  const awaiting = requests.filter(r =>
    hasInvoiceableLines(r) || (requestInvoices(r).length > 0 && isStillOnOrder(r))
  );
  const discrepancies = requests.filter(hasOpenDiscrepancy);
  // Settled is closed history. Capped rather than unbounded, and the cap is
  // announced below the list rather than left to read as the whole truth.
  const settledAll = requests.filter(r => r.status === 'closed');
  const settled = settledAll.slice(0, SETTLED_LIMIT);

  // Partially invoiced: money has been paid against this order and more is still
  // billable. Distinct from "awaiting", which includes orders never invoiced.
  const partiallyInvoiced = awaiting.filter(r => requestInvoices(r).length > 0).length;

  // First-pass match rate — the share of invoices that reconciled with no manual
  // intervention. Described in the AP literature as the single best measure of
  // process health, and accepted as an executive KPI at 74y's gate. Computed
  // from what the invoice rows already record, never estimated: an invoice that
  // was not flagged matched on the first pass.
  const allInvoices = requests.flatMap(requestInvoices);
  // #301, Session 74z4. The rule moved to invoiceDiscrepancy.js because two
  // things read it now and they must not disagree about what it means. An
  // invoice resolved as flagged-in-error leaves the calculation entirely; one
  // resolved as price-accepted stays counted against it, because it genuinely
  // did require manual intervention. Resolving never clears discrepancy_flagged,
  // which is what keeps this number from rising every time somebody does work.
  const firstPassRate = computeFirstPassRate(allInvoices);

  // #300. Recomputed on every render rather than memoised on a date: the buckets
  // are relative to today, and a value frozen at mount would be wrong for
  // anyone who leaves this page open across midnight — which an AP clerk does.
  const aging = computeAging(allInvoices, Date.now());

  // #298, 78n9.4. The set gate screen 3 calls "Unexported obligations", and it
  // is the SAME predicate the aging strip narrows on — extracted to
  // invoiceDocuments.js so the strip above the list and the list itself cannot
  // drift apart about what "unexported" means.
  //
  // Each row is paired with the order it belongs to, because a document needs
  // both: the invoice carries the money, the order carries the lines, the
  // vendor and the branch.
  const unexportedRows = requests.flatMap(r =>
    requestInvoices(r).filter(isUnexported).map(invoice => ({ invoice, request: r }))
  );

  function selectTab(id) {
    setActiveTab(id);
    setSelectedReq(null);
    // The selection does not survive a tab change. It is only meaningful on the
    // tab that shows it, and a selection carried invisibly to another tab is a
    // batch export nobody can see the contents of.
    setSelectedInvoiceIds(new Set());
  }

  // Ordered as the work runs, corrected at Session 78n9.4 on Colby's question:
  // a mismatch is resolved BEFORE the invoice goes to QuickBooks, and the tabs
  // read left to right in that order. Export sat second until this session,
  // which read as "send it, then find out whether it matched".
  //
  // `short` is the mobile label, and the pattern is AGING_BUCKETS' own, three
  // hundred lines up. It exists because measurement forced it: the four full
  // labels are 409px in a 347px rail at 375px wide, so Settled was off screen
  // and reachable only by a side scroll a new user has no reason to try. The
  // short set is 330px even at three-digit counts.
  //
  // SETTLED IS NOW CLOSED, and that is the substantive change rather than the
  // cosmetic one. An order can be fully received and fully billed while its
  // invoice has never reached QuickBooks -- on staging at this session every
  // one of the nine unexported invoices sat on a closed order -- so "Settled"
  // claimed nothing more was owed about the exact money the Unexported tab was
  // still asking for. "Closed" claims only what is true, and it is the word
  // material_requests.status already stores.
  const tabs = [
    { id: 'awaiting', label: 'Awaiting invoice', short: 'Awaiting', count: awaiting.length },
    { id: 'discrepancies', label: 'Discrepancies', short: 'Flagged', count: discrepancies.length },
    { id: 'unexported', label: 'Unexported', short: 'To export', count: unexportedRows.length },
    { id: 'settled', label: 'Closed', short: 'Closed', count: null },
  ];

  const listForTab = activeTab === 'awaiting' ? awaiting
    : activeTab === 'unexported' ? unexportedRows
      : activeTab === 'discrepancies' ? discrepancies
        : settled;

  // #304 D6, Session 79.14d. This was a hand-maintained list and it had drifted
  // from the Requests inbox's copy in BOTH directions — this one searched the
  // vendor's order number and that one did not, that one searched the work order
  // and this one did not, and NEITHER searched the carrier's tracking number.
  // One shared list now, so "whichever number you are holding, the order comes
  // back" is true on every screen rather than nearly true on each.
  //
  // `invoice_ref` was added to the select above. This page loads the invoice
  // ROWS and could already reach every bill through them; the header column is a
  // mirror of the most recent one and costs nothing to carry. The Unexported
  // predicate below still reads the invoice row directly, which is right — that
  // tab holds invoices rather than orders.
  const term = search.trim().toLowerCase();
  const matchesTerm = r => matchesOrderSearch(r, search);

  // #298, 78n9.4. The Unexported tab holds { invoice, request } pairs rather
  // than orders, so it needs its own predicate — and it gains one field the
  // order tabs cannot offer: the vendor's own invoice number, which is the
  // thing a person holding a paper bill actually has in their hand.
  const matchesUnexported = row =>
    (row.invoice?.invoice_ref || '').toLowerCase().includes(term)
    || matchesTerm(row.request || {});

  const onTabMatches = !term ? listForTab
    : activeTab === 'unexported' ? listForTab.filter(matchesUnexported)
      : listForTab.filter(matchesTerm);

  // #296's second half, Session 74z2. Search used to filter the current tab
  // only, so an order sitting on a different tab returned nothing and read as
  // an order that does not exist. That is how the fall-through row above stayed
  // invisible even to someone looking for it by number.
  //
  // It only widens when the current tab has NO match, so the ordinary case is
  // unchanged and nobody gets results from a tab they did not ask about.
  //
  // The Unexported tab is deliberately outside this, in BOTH directions. #296's
  // widening is about an ORDER hiding on a tab you are not looking at; an
  // invoice row is a different entity, and mixing the two would put order cards
  // under a heading that promises invoices you can tick. It keeps its own
  // search instead, which is strictly better for the thing being looked for
  // there — it matches the vendor's invoice number.
  const OTHER_TAB_LABEL = { awaiting: 'Awaiting invoice', unexported: 'Unexported', discrepancies: 'Discrepancies', settled: 'Closed' };
  const otherTabMatches = (term && onTabMatches.length === 0 && activeTab !== 'unexported')
    ? [
      ...(activeTab !== 'awaiting' ? awaiting : []),
      ...(activeTab !== 'discrepancies' ? discrepancies : []),
      ...(activeTab !== 'settled' ? settled : []),
    ].filter(matchesTerm)
    : [];

  const visibleList = onTabMatches.length === 0 && otherTabMatches.length > 0
    ? otherTabMatches
    : onTabMatches;

  const searchWidened = otherTabMatches.length > 0 && onTabMatches.length === 0;

  // Read the live row so a receive landing elsewhere while the sheet is open
  // updates the quantities being reconciled. Falls back to the snapshot after
  // submit, when the request may have become 'closed' — which is what keeps the
  // success screen rendered.
  const liveSelectedReq = selectedReq ? (requests.find(r => r.id === selectedReq.id) || selectedReq) : null;
  const liveMobileDetailReq = mobileDetailReq ? (requests.find(r => r.id === mobileDetailReq.id) || mobileDetailReq) : null;
  const liveInvoiceReq = invoiceReq ? (requests.find(r => r.id === invoiceReq.id) || invoiceReq) : null;


  const sharedInvoiceProps = {
    visible: showEnterInvoice, req: liveInvoiceReq, isMobile,
    invoiceItemPrices, setInvoiceItemPrice,
    invoiceRef, setInvoiceRef: setInvoiceRefAndCheck,
    // #290. The check runs on blur rather than per keystroke: an invoice number
    // is meaningless half-typed, and a query per character would warn about a
    // prefix that is not the number being entered.
    onInvoiceRefBlur: () => checkDuplicateInvoiceRef(invoiceRef, liveInvoiceReq?.vendor_id, liveInvoiceReq?.id),
    dupeMatch, dupeChecking,
    invoiceDate, setInvoiceDate: setInvoiceDateAndDue,
    invoiceTerms, setInvoiceTerms: setInvoiceTermsAndDue,
    invoiceDueDate, setInvoiceDueDate,
    invoiceTax, setInvoiceTax,
    invoiceSaving, invoiceError, invoiceDone, invoiceFlagged, invoiceCost,
    onSubmit: submitEnterInvoice,
    onClose: () => setShowEnterInvoice(false),
  };

  const closeProps = {
    canShortClose,
    onShortClose: item => openShortClose(liveSelectedReq || liveMobileDetailReq, item),
    onReopen: reopenLineForBilling,
  };

  const sharedShortCloseProps = {
    visible: !!shortCloseItem,
    flag: INVOICE,
    item: shortCloseItem,
    req: shortCloseReq,
    isMobile,
    saving: shortCloseSaving,
    error: shortCloseError,
    done: shortCloseDone,
    result: shortCloseResult,
    onSubmit: submitShortClose,
    onClose: closeShortCloseSheet,
  };

  // #301, Session 74z4. Mounted at both breakpoints for the reason
  // sharedShortCloseProps exists: the desktop panel and the mobile sheet are
  // different trees, and a sheet mounted in only one of them is invisible on the
  // other surface with nothing on screen to say why.
  // #299, Session 74z5 — the credit flow.
  function closeCreditSheet() {
    setCreditReq(null);
    setCreditInvoice(null);
    setCreditLines([]);
    setCreditError('');
    setCreditDone(false);
    setCreditResult(null);
  }

  // #298, Session 78n9.3 — Send to QuickBooks.
  //
  // The vendor name rides along on the invoice object because the sheet has no
  // request in scope, and showing a supplier by its QuickBooks id would defeat
  // the point of the confirmation: the whole job of that screen is letting
  // Bridgette recognise who is about to be billed.
  function openSendToQb(req, inv) {
    setQbInvoice({ ...inv, __vendorName: req?.inventory_vendors?.name || null });
  }

  // Re-fetching rather than patching the row in place, deliberately. The send
  // writes five columns server-side — the bill id, the realm, and three about
  // who exported it — and a local guess at those would be a second, quietly
  // different copy of what the server actually recorded.
  //
  // IT DOES NOT CLOSE THE SHEET, and the first version did. Found in the live
  // test: pressing Send made the whole sheet vanish, so the confirmation naming
  // the QuickBooks reference was rendered into a component that had already
  // unmounted and could never be read. The only feedback was an aging total
  // quietly dropping by the invoice's own value, which is not confirmation that
  // a bill was created — it is something you would have to already know to
  // notice.
  //
  // Same shape as the result banner at 78n8.2 that setMoDone(true) guaranteed
  // nobody could see. The sheet closes when the person closes it.
  function afterQbSend() {
    load();
  }

  // #298, 78n9.4.
  //
  // THIS DELIBERATELY DOES NOT MARK ANYTHING EXPORTED, and the omission is the
  // decision rather than an oversight. `exported_at` means the obligation has
  // reached the accounting system — it is what the aging strip narrows on, and
  // what stops an invoice being pushed to QuickBooks twice. Producing a PDF is
  // not that handoff: the money is still owed and QuickBooks still has not been
  // told. Setting it here would quietly remove real payables from the strip
  // above this list and understate what D&R owes, which is a money-path defect
  // dressed as a tidy-up.
  //
  // What IS recorded is who produced which file over which invoices, in
  // export_log. That is the audit trail #395 closes on, and it is a different
  // fact from whether the bill has been entered.
  function afterBatchExport(layout, count) {
    setSelectedInvoiceIds(new Set());
    setBatchNote(
      (layout === 'register' ? 'Register' : count + (count === 1 ? ' document' : ' documents'))
      + ' downloaded. These invoices stay on this tab — the file is a document, not a handoff to QuickBooks.'
    );
  }

  async function openCredit(req, inv) {
    setCreditReq(req);
    setCreditInvoice(inv);
    setCreditError('');
    setCreditDone(false);
    setCreditResult(null);
    setCreditLines([]);
    setCreditLinesLoading(true);
    try {
      // The invoice's own lines AND its credits' lines: the per-line ceiling is
      // what that invoice billed for a line minus what has already been credited
      // against it, so both sides are needed to compute it.
      const creditIds = requestInvoices(req)
        .filter(d => isCredit(d) && String(d.credit_of_invoice_id) === String(inv.id))
        .map(d => d.id);

      const { data, error: err } = await supabase
        .from('material_request_invoice_lines')
        .select('id, invoice_id, request_item_id, qty_invoiced, line_amount')
        .in('invoice_id', [inv.id, ...creditIds]);
      if (err) throw err;

      // Labelled from the request's own items, already loaded, rather than by
      // embedding inventory_items two hops down from the line.
      const items = req.material_request_items || [];
      setCreditLines((data || []).map(l => ({
        ...l,
        label: itemLabel(items.find(i => String(i.id) === String(l.request_item_id))),
      })));
    } catch (e) {
      setCreditError('Could not load the invoice lines: ' + e.message);
    } finally {
      setCreditLinesLoading(false);
    }
  }

  async function submitCredit({ creditRef, creditDate, items }) {
    if (!creditInvoice || !creditReq) return;
    setCreditSaving(true);
    setCreditError('');
    try {
      const result = await enterVendorCredit({
        requestId: creditReq.id,
        userId: profile.id,
        creditOfInvoiceId: creditInvoice.id,
        items,
        creditRef,
        creditDate,
      });
      setCreditResult(result);
      setCreditDone(true);
      await load();
      // A credit changes what the order invoiced for, which changes the
      // Discrepancies and Settled counts App.js renders on the sidebar. Same
      // escape hatch submitEnterInvoice and the short-close path use.
      onCountsChanged?.();
    } catch (e) {
      setCreditError('Could not record the credit: ' + e.message);
    } finally {
      setCreditSaving(false);
    }
  }

  const sharedCreditProps = {
    visible: !!creditInvoice,
    invoice: creditInvoice,
    req: creditReq,
    docs: creditReq ? requestInvoices(creditReq) : [],
    lines: creditLines,
    linesLoading: creditLinesLoading,
    isMobile,
    saving: creditSaving,
    error: creditError,
    done: creditDone,
    result: creditResult,
    onSubmit: submitCredit,
    onClose: closeCreditSheet,
  };

  const sharedResolveProps = {
    visible: !!resolveInvoice,
    invoice: resolveInvoice,
    req: resolveReq,
    isMobile,
    saving: resolveSaving,
    error: resolveError,
    done: resolveDone,
    result: resolveResult,
    onSubmit: submitResolve,
    onClose: closeResolveSheet,
  };

  // Both halves are per-tab. The subtitle was generic in the first cut and said
  // "orders appear here once something has been received against them" under the
  // Discrepancies heading, which is not how an order gets onto that tab - it
  // gets there by being billed outside the tolerance. An empty state that
  // explains the wrong mechanism is worse than none, because it is the one thing
  // on screen when there is nothing else to correct it.
  const EMPTY_STATES = {
    awaiting: [
      'Nothing is waiting to be invoiced',
      'Orders appear here once something has been received against them',
    ],
    discrepancies: [
      'No open discrepancies',
      'An invoice billed outside the tolerance lands here for review',
    ],
    unexported: [
      'Nothing is waiting to be exported',
      'Every invoice recorded so far has been sent to QuickBooks or exported as a document',
    ],
    settled: [
      'No closed orders yet',
      'Orders appear here once every line has been received and billed',
    ],
  };
  const [emptyLabel, emptySub] = EMPTY_STATES[activeTab];

  // #298, 78n9.4. Selection helpers. The Set is replaced rather than mutated —
  // mutating it in place and calling the setter with the same reference is a
  // render React is entitled to skip, and the checkbox would go dead.
  function toggleInvoice(id) {
    setSelectedInvoiceIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  // Selected rows, in the order they appear on screen, and narrowed to what is
  // actually VISIBLE. A row selected before a search was typed must not travel
  // into the file invisibly — the export would then contain something the
  // person could not see when they pressed the button.
  const selectedRows = (activeTab === 'unexported' ? onTabMatches : [])
    .filter(row => selectedInvoiceIds.has(row.invoice.id));
  const selectedTotal = selectionTotal(selectedRows.map(r => r.invoice));

  const listBody = (
    <>
      {/* #296. Says plainly that the results came from elsewhere, rather than
          silently showing rows the tab does not contain — which would be its own
          kind of lie, one tab over. */}
      {searchWidened && (
        <div style={{
          background: MAGENTA_SOFT, border: '1px solid ' + MAGENTA_LINE, borderRadius: 8,
          padding: '8px 11px', marginBottom: 8, fontSize: 11.5, color: '#7A1355', lineHeight: 1.5,
        }}>
          Nothing on <b>{OTHER_TAB_LABEL[activeTab]}</b> matches “{search.trim()}”.
          Showing {otherTabMatches.length} {otherTabMatches.length === 1 ? 'match' : 'matches'} from the other tabs.
        </div>
      )}
      {/* Rendered where the person is looking after the sheet closes, and it
          says the one thing the download itself cannot: that the rows are still
          here on purpose. 78n9.3 shipped a confirmation into a sheet that had
          already unmounted; this one lives on the page that survives. */}
      {activeTab === 'unexported' && batchNote && (
        <div style={{
          background: MAGENTA_SOFT, border: '1px solid ' + MAGENTA_LINE, borderRadius: 8,
          padding: '9px 11px', marginBottom: 8, fontSize: 11.5, color: '#7A1355', lineHeight: 1.5,
          display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'flex-start',
        }}>
          <span>{batchNote}</span>
          <button
            onClick={() => setBatchNote('')}
            aria-label="Dismiss"
            style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: '#7A1355', fontSize: 15, lineHeight: 1, padding: 0 }}>
            ×
          </button>
        </div>
      )}
      {/* #431. The tab named for the job said nothing about the job until a row
          was ticked -- the selection bar does not exist at zero selected, so a
          new user landing here saw a list of invoices and no mention of
          QuickBooks anywhere on screen. Colby found this by asking which tab
          Bridgette would send from and whether anyone would know.

          It states both actions, because the tab genuinely has two, and it
          survives a search: it describes the tab, not the selection. */}
      {activeTab === 'unexported' && visibleList.length > 0 && !batchNote && (
        <div style={{
          background: MAGENTA_SOFT, border: '1px solid ' + MAGENTA_LINE, borderRadius: 8,
          padding: '9px 11px', marginBottom: 8, fontSize: 11.5, color: '#7A1355', lineHeight: 1.5,
        }}>
          These invoices are not in QuickBooks yet. Tick one to send it, or tick several to
          export them as documents.
        </div>
      )}
      {/* Not in the gate's sketch, and it earns its place: the standard is
          every action in two taps, and ticking forty rows one at a time to run
          a month-end register is not that. It selects what is VISIBLE, so it
          respects a search rather than quietly reaching past it. */}
      {activeTab === 'unexported' && visibleList.length > 1 && (
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '0 3px 8px', fontSize: 11.5, color: '#7A7A7A',
        }}>
          <span>{visibleList.length} unexported</span>
          <button
            onClick={() => {
              const ids = visibleList.map(row => row.invoice.id);
              const allOn = ids.every(id => selectedInvoiceIds.has(id));
              setSelectedInvoiceIds(prev => {
                const next = new Set(prev);
                ids.forEach(id => (allOn ? next.delete(id) : next.add(id)));
                return next;
              });
            }}
            style={{
              border: 'none', background: 'transparent', cursor: 'pointer',
              fontSize: 11.5, fontWeight: 700, color: MAGENTA, padding: '2px 0',
            }}>
            {visibleList.every(row => selectedInvoiceIds.has(row.invoice.id)) ? 'Clear all' : 'Select all'}
          </button>
        </div>
      )}
      {activeTab === 'unexported'
        ? visibleList.map(row => (
          <UnexportedInvoiceRow
            key={row.invoice.id}
            invoice={row.invoice}
            request={row.request}
            branchName={branchName(row.request?.branch_id)}
            checked={selectedInvoiceIds.has(row.invoice.id)}
            onToggle={toggleInvoice}
          />
        ))
        : visibleList.map(r => (
          <InvoiceRequestCard
            key={r.id}
            req={r}
            isMobile={isMobile}
            selectedReq={selectedReq}
            branchName={branchName(r.branch_id)}
            onSelect={rr => (isMobile ? setMobileDetailReq(rr) : setSelectedReq(rr))}
          />
        ))}
      {visibleList.length === 0 && (
        <div style={{ background: 'white', borderRadius: 10, padding: '32px 16px', border: '1px solid #E8E8E8', textAlign: 'center' }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#9A9A9A' }}>{term ? 'No match' : emptyLabel}</div>
          <div style={{ fontSize: 11, color: '#C0C0C0', marginTop: 4 }}>
            {/* #296. Was "nothing on this tab", which was true and misleading:
                search now widens across all three, so an empty result means the
                order is not on any of them. */}
            {term ? 'No order on any tab matches “' + search.trim() + '”' : emptySub}
          </div>
        </div>
      )}
      {/* #432, Session 78n9.4, raised by Colby: the export controls get buried
          on a long list.

          THIS WAS STICKY AND STICKY DOES NOT DO WHAT I CLAIMED IT DID. A
          `position: sticky` element with `bottom` cannot pull itself UP into
          view -- it only stops scrolling away once you have already reached it.
          So at the top of the list the bar sat at y=1277 in an 812px viewport,
          exactly as buried as a static element, and the comment that used to be
          here asserted the opposite. Measured, not reasoned: nine invoices put
          it 1277px down, two dozen would put it past 3000.

          Docked instead, which is what Gmail, Files and every photo picker do
          with a batch selection. Locked rule 3 bans `position: fixed` in
          OVERLAYS -- that rule exists because overlays must go through
          Overlay.js's portal -- and this is a page-level action bar, of which
          the app already ships one: nav.bottom-nav, 64px tall at z-index 150.
          This sits above it at 72px and below it in z-order, so the two never
          fight and the nav stays reachable. */}
      {activeTab === 'unexported' && selectedRows.length > 0 && (
        <div style={isMobile
          ? { position: 'fixed', left: 12, right: 12, bottom: 72, zIndex: 120 }
          : { position: 'sticky', bottom: 0, zIndex: 3, paddingTop: 8 }}>
          <SelectionBar
            count={selectedRows.length}
            total={selectedTotal}
            isMobile={isMobile}
            canSendQb={canReconcile}
            onClear={() => setSelectedInvoiceIds(new Set())}
            onExport={() => setShowBatchSheet(true)}
            onSendQb={() => selectedRows.length === 1
              && openSendToQb(selectedRows[0].request, selectedRows[0].invoice)}
          />
        </div>
      )}
      {activeTab === 'settled' && settledAll.length > SETTLED_LIMIT && (
        <div style={{ fontSize: 11, color: '#9A9A9A', textAlign: 'center', padding: '10px 0' }}>
          Showing the {SETTLED_LIMIT} most recently updated of {settledAll.length} closed orders. Search to find an older one.
        </div>
      )}
    </>
  );

  const searchBox = (
    <div style={{ position: 'relative', marginBottom: 10 }}>
      <IconSearch size={14} color="#9A9A9A" style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} />
      <input
        type="text" value={search} onChange={e => setSearch(e.target.value)}
        placeholder="Search PO number, order or vendor..."
        style={{ width: '100%', height: 38, border: '1px solid #E0E0E0', borderRadius: 8, padding: '0 11px 0 32px', color: '#0F0F0F', background: 'white', boxSizing: 'border-box' }}
      />
    </div>
  );

  // #432. The docked bar floats over the list, so the page reserves room for it
  // or it covers the last rows -- and the last rows are the ones somebody
  // scrolled all that way to reach. 80 is the nav's own reservation; the extra
  // is the bar, and it only applies while the bar exists.
  const mobilePadBottom = (activeTab === 'unexported' && selectedRows.length > 0) ? 210 : 80;

  if (isMobile) {
    return (
      <div style={{ background: '#F5F5F3', minHeight: '100vh', paddingBottom: mobilePadBottom }}>
        <div style={{ position: 'sticky', top: 'var(--topbar-height)', zIndex: 10, background: 'white', borderBottom: '1px solid #E8E8E8', padding: '8px 14px', display: 'flex', gap: 10, alignItems: 'center' }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: MAGENTA }}>{awaiting.length} awaiting</span>
          {discrepancies.length > 0 && (
            <>
              <span style={{ color: '#D0D0D0', fontSize: 12 }}>·</span>
              <span style={{ fontSize: 12, fontWeight: 600, color: '#A32D2D' }}>{discrepancies.length} flagged</span>
            </>
          )}
          {firstPassRate != null && (
            <>
              <span style={{ color: '#D0D0D0', fontSize: 12 }}>·</span>
              <span style={{ fontSize: 12, fontWeight: 600, color: '#7A7A7A' }}>{firstPassRate}% first-pass</span>
            </>
          )}
        </div>

        <AgingStrip aging={aging} isMobile />

        <TabBar tabs={tabs} activeTab={activeTab} setActiveTab={selectTab} isMobile discrepancyTabRef={discrepancyTabRef} />

        {/* Callouts gate Wave 2 — inq_tabs_first. The densest screen in the app:
            three tabs, an aging strip and a match rate. Nothing on it says what a
            discrepancy IS or that clearing one requires a stated reason. */}
        <OnboardingCallout
          calloutKey="inq_tabs_first"
          text="Discrepancies are invoices priced differently from the order. Resolve each one with a reason."
          anchorRef={null}
          isMobile={true}
          show={canReconcile}
        />

        <div style={{ padding: '10px 12px' }}>
          {searchBox}
          {loading ? (
            <div style={{ textAlign: 'center', padding: 32, color: '#9A9A9A', fontSize: 13 }}>Loading...</div>
          ) : error ? (
            <div style={{ background: '#FFEBEE', color: '#A32D2D', borderRadius: 8, padding: '10px 12px', fontSize: 12 }}>{error}</div>
          ) : listBody}
        </div>

        <EnterInvoiceSheet {...sharedInvoiceProps} />
        {mobileDetailReq && (
          <MobileInvoiceDetailSheet
            req={liveMobileDetailReq}
            branchName={branchName(liveMobileDetailReq?.branch_id)}
            onClose={() => setMobileDetailReq(null)}
            onEnterInvoice={openEnterInvoice}
            canReconcile={canReconcile}
            canSeeContact={canSeeContact}
            closeProps={closeProps}
            resolveProps={{ onResolve: openResolve }}
            creditProps={{ onCredit: openCredit }}
            qbProps={canReconcile ? { onSend: openSendToQb } : null}
          />
        )}
        <ShortCloseSheet {...sharedShortCloseProps} />
        <ResolveDiscrepancySheet {...sharedResolveProps} />
      <RecordCreditSheet {...sharedCreditProps} />
      {/* Mounted in BOTH trees for the reason sharedShortCloseProps records:
          the desktop panel and the mobile sheet are different trees, and a
          sheet mounted in only one is invisible on the other surface with
          nothing on screen to say why. */}
      {qbInvoice && (
        <SendToQuickBooksSheet
          invoice={qbInvoice}
          isMobile={isMobile}
          onClose={() => setQbInvoice(null)}
          onSent={afterQbSend}
        />
      )}
      {/* #298, 78n9.4. Mounted in BOTH trees for the same reason every other
          sheet on this page is: they are different trees, and a sheet mounted
          in one is invisible on the other surface with nothing to say why. */}
      {showBatchSheet && selectedRows.length > 0 && (
        <BatchDocumentsSheet
          isMobile={isMobile}
          selection={selectedRows}
          settings={{ company_name: getCompanyName() }}
          branchNameFor={branchName}
          profile={profile}
          scopeLabel="Unexported obligations"
          search={search.trim()}
          onClose={() => setShowBatchSheet(false)}
          onExported={afterBatchExport}
        />
      )}
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 56px)', overflow: 'hidden' }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', borderBottom: '1px solid #E8E8E8', background: '#FAFAF9', flexShrink: 0 }}>
        <StatCell value={awaiting.length} label="Awaiting invoice" color={awaiting.length > 0 ? MAGENTA : '#0F0F0F'} />
        <StatCell value={partiallyInvoiced} label="Partially invoiced" />
        <StatCell value={discrepancies.length} label="Discrepancies" color={discrepancies.length > 0 ? '#A32D2D' : '#0F0F0F'} />
        <StatCell value={firstPassRate != null ? firstPassRate + '%' : '—'} label="First-pass match" last />
      </div>

      <AgingStrip aging={aging} isMobile={false} />

      <TabBar tabs={tabs} activeTab={activeTab} setActiveTab={selectTab} isMobile={false} discrepancyTabRef={discrepancyTabRef} />

      {/* Callouts gate Wave 2 — inq_tabs_first. */}
      <OnboardingCallout
        calloutKey="inq_tabs_first"
        text="Discrepancies are invoices priced differently from the order. Resolve each one with a reason."
        anchorRef={discrepancyTabRef}
        isMobile={false}
        show={canReconcile}
      />

      <div style={{ display: 'grid', gridTemplateColumns: '420px 1fr', flex: 1, overflow: 'hidden' }}>
        <div style={{ borderRight: '1px solid #E8E8E8', overflowY: 'auto', padding: '14px 14px 80px', background: '#F7F7F6' }}>
          {searchBox}
          {loading ? (
            <div style={{ textAlign: 'center', padding: 32, color: '#9A9A9A', fontSize: 13 }}>Loading...</div>
          ) : error ? (
            <div style={{ background: '#FFEBEE', color: '#A32D2D', borderRadius: 8, padding: '10px 12px', fontSize: 12 }}>{error}</div>
          ) : listBody}
        </div>

        <div style={{ overflowY: 'auto' }}>
          <InvoiceDetailPanel
            req={liveSelectedReq}
            emptyHint={activeTab === 'unexported'
              ? 'Tick the invoices you need, then Export or send one to QuickBooks'
              : undefined}
            branchName={branchName(liveSelectedReq?.branch_id)}
            onEnterInvoice={openEnterInvoice}
            canReconcile={canReconcile}
            canSeeContact={canSeeContact}
            closeProps={closeProps}
            resolveProps={{ onResolve: openResolve }}
            creditProps={{ onCredit: openCredit }}
            qbProps={canReconcile ? { onSend: openSendToQb } : null}
          />
        </div>
      </div>

      <EnterInvoiceSheet {...sharedInvoiceProps} />
      <ShortCloseSheet {...sharedShortCloseProps} />
      <ResolveDiscrepancySheet {...sharedResolveProps} />
      <RecordCreditSheet {...sharedCreditProps} />
      {/* Mounted in BOTH trees for the reason sharedShortCloseProps records:
          the desktop panel and the mobile sheet are different trees, and a
          sheet mounted in only one is invisible on the other surface with
          nothing on screen to say why. */}
      {qbInvoice && (
        <SendToQuickBooksSheet
          invoice={qbInvoice}
          isMobile={isMobile}
          onClose={() => setQbInvoice(null)}
          onSent={afterQbSend}
        />
      )}
      {/* #298, 78n9.4. Mounted in BOTH trees for the same reason every other
          sheet on this page is: they are different trees, and a sheet mounted
          in one is invisible on the other surface with nothing to say why. */}
      {showBatchSheet && selectedRows.length > 0 && (
        <BatchDocumentsSheet
          isMobile={isMobile}
          selection={selectedRows}
          settings={{ company_name: getCompanyName() }}
          branchNameFor={branchName}
          profile={profile}
          scopeLabel="Unexported obligations"
          search={search.trim()}
          onClose={() => setShowBatchSheet(false)}
          onExported={afterBatchExport}
        />
      )}
    </div>
  );
}
